import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Pie, Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
} from "chart.js";
import $ from "jquery";
import moment from "moment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import "bootstrap/dist/css/bootstrap.min.css";
import "daterangepicker/daterangepicker.css";
import "daterangepicker";
import "select2";
import "select2/dist/css/select2.min.css";
import {
  zoneMasterAPI,
  complaintAPI,
  masterDataAPI,
  territoryMasterAPI,
} from "../services/api";
import { adminDashboardAPI } from "../services/adminDashboardApi";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import {
  parseApiEnvelope,
  extractZonesFromResponse,
  extractRegionsFromResponse,
  normalizeZoneRow,
  normalizeRegionRow,
  normalizeTerritoryRow,
  mapZoneToOption,
  mapExportZonesToOptions,
  mapRegionToOption,
  mapTerritoryToOption,
  formatZoneOptionLabel,
  isExportMarketSelected,
  joinGeoFilter,
  resolveRegionGeoCandidates,
  expandZoneFilterCodes,
  getCountryCodesForSelectedZones,
  resolveZoneCodeForCountry,
} from "../utils/dashboardGeoFilterUtils";
import {
  isGeoAllSelectionToken,
  USER_DEFAULT_GEO_CODE,
} from "../utils/userTerritoryScope";
import DashboardExportDropdown from "../components/common/DashboardExportDropdown";
import {
  formatDateDDMMYYYY,
  exportJsonToExcel,
  exportRowsToCsv,
  exportRowsToPdf,
} from "../utils/dashboardExportUtils";
import {
  getDashboardDateRangePresets,
  getDashboardFinancialYearRange,
  initDashboardCardDateRangePicker,
  getChartDateInputWidthPx,
} from "../utils/dashboardDateRangePicker";
import {
  ADMIN_CHART_DATE_KEYS,
  ADMIN_KPI_WIDGET_KEY,
  createAdminChartCardDates,
  createAdminChartDatePickerRefs,
  createAdminWidgetLoadingState,
} from "../utils/adminDashboardChartDateConfig";
import {
  saveAdminDashboardState,
  readAdminDashboardState,
  readAdminDashboardRestoreSnapshot,
  markAdminDashboardRestored,
} from "../utils/adminDashboardState";
import { clearAdminServiceRequestListState } from "../utils/adminServiceRequestListState";
import "./AdminDashboard.css";

/** Master-data / filter sentinel for Country/Zone/Region/Territory "All". */
const GEO_ALL_OPTION = { id: USER_DEFAULT_GEO_CODE, text: "All" };

const isGeoAllOptionRow = (id, text) => {
  if (isGeoAllSelectionToken(id)) return true;
  return String(text ?? "").trim().toLowerCase() === "all";
};

/** Chart label: invoice zone (SAP salesDistrict). Hide geo-master sentinel "All". */
const displayInvoiceZoneName = (raw) => {
  const n = String(raw ?? "").trim();
  if (!n || n === "–" || n.toLowerCase() === "all" || n === "0000") return "Unassigned";
  return n;
};

const selectionIncludesGeoAll = (selected, optionList = []) => {
  const ids = Array.isArray(selected) ? selected : [];
  return ids.some((id) => {
    const sid = String(id ?? "").trim();
    if (!sid) return false;
    if (isGeoAllSelectionToken(sid)) return true;
    const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === sid);
    return isGeoAllOptionRow(sid, opt?.text);
  });
};

const isGeoAllFilterValue = (val) =>
  Array.isArray(val) &&
  val.length > 0 &&
  val.every((id) => isGeoAllSelectionToken(id));

/** Omit All/0000 from API geo params (NULL = no geo restriction). */
const joinGeoFilterForApi = (val) => {
  if (isGeoAllFilterValue(val)) return null;
  if (Array.isArray(val) && val.some(isGeoAllSelectionToken)) {
    const real = val.filter((id) => !isGeoAllSelectionToken(id));
    return joinGeoFilter(real);
  }
  return joinGeoFilter(val);
};

// Plugin to display values on pie chart segments
const pieChartValuePlugin = {
  id: "pieChartValuePlugin",
  afterDatasetsDraw: (chart) => {
    if (chart.config.type !== "pie" && chart.config.type !== "doughnut") {
      return;
    }
    const ctx = chart.ctx;
    const data = chart.data.datasets[0].data;
    const meta = chart.getDatasetMeta(0);
    meta.data.forEach((element, index) => {
      const value = data[index];
      const position = element.tooltipPosition();
      ctx.save();
      ctx.fillStyle = "#333";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(value, position.x, position.y);
      ctx.restore();
    });
  },
};

// Plugin to display values on bar chart - positioned in center of bars (same as ApproverDashboard)
const barChartValuePlugin = {
  id: "barChartValuePlugin",
  afterDatasetsDraw: (chart) => {
    // Only apply to bar charts
    if (chart.config.type !== "bar") {
      return;
    }

    const ctx = chart.ctx;
    const data = chart.data.datasets[0].data;
    const meta = chart.getDatasetMeta(0);

    meta.data.forEach((element, index) => {
      const value = data[index];
      if (value === null || value === undefined) return;

      const { x, y } = element.tooltipPosition();
      const barHeight = element.height;
      const barY = y - barHeight;

      ctx.save();
      ctx.fillStyle = "#333";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Display value - center of the bar (same style as pie chart)
      const textY = barY + barHeight / 2;
      ctx.fillText(value, x, textY);
      ctx.restore();
    });
  },
};

// Register Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  pieChartValuePlugin,
  barChartValuePlugin
);

// Gauge Chart Component
const GaugeChart = ({ value, max = 100, title }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  const color = percentage >= 80 ? "#50c878" : percentage >= 60 ? "#ff9800" : "#dc3545";

  return (
    <div style={{ textAlign: "center", padding: "20px" }}>
      <svg width="200" height="120" viewBox="0 0 200 120">
        {/* Background arc */}
        <circle
          cx="100"
          cy="100"
          r={radius}
          fill="none"
          stroke="#e0e0e0"
          strokeWidth="20"
          strokeLinecap="round"
          transform="rotate(-90 100 100)"
        />
        {/* Value arc */}
        <circle
          cx="100"
          cy="100"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="20"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 100 100)"
        />
      </svg>
      <div style={{ marginTop: "-80px", fontSize: "24px", fontWeight: "bold", color: "#333" }}>
        {value}%
      </div>
      <div style={{ fontSize: "14px", color: "#666", marginTop: "5px" }}>{title}</div>
    </div>
  );
};

/** Admin dashboard-v2 list endpoints: accept camelCase or PascalCase (matches /csrp/approver-dashboard JsonResult). */
function parseAdminDashboardListResponse(response) {
  if (!response) return [];
  const ok = response.success === true || response.Success === true;
  if (!ok) return [];
  const raw = response.data !== undefined ? response.data : response.Data;
  return Array.isArray(raw) ? raw : [];
}

function getAdminDashboardDataPayload(response) {
  if (!response) return null;
  const ok = response.success === true || response.Success === true;
  if (!ok) return null;
  const raw = response.data !== undefined ? response.data : response.Data;
  return raw && typeof raw === "object" ? raw : null;
}

/** Admin dashboard loads Domestic (M001) by default on first paint. */
const DEFAULT_ADMIN_DASHBOARD_MARKET = "M001";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const openVsClosedChartRef = useRef(null);
  const categoryChartRef = useRef(null);
  const productChartRef = useRef(null);
  const zoneChartRef = useRef(null);
  const avgTATByZoneChartRef = useRef(null);
  const repeatComplaintsChartRef = useRef(null);
  const settlementTypeChartRef = useRef(null);
  const maxQueriesChartRef = useRef(null);
  const dateRangePickerRef = useRef(null);
  const filterDateRangePickerRef = useRef(null);
  const skipDateFilterDashboardLoadRef = useRef(true);
  const chartDatePickerRefs = useRef(createAdminChartDatePickerRefs());
  const chartLoaderRefs = useRef({});
  const chartCardDatesRef = useRef(null);

  // Refs for select2 dropdowns
  const statusSelectRef = useRef(null);
  const bvSelectRef = useRef(null);
  const zoneOptionsRef = useRef([]);
  const regionOptionsRef = useRef([]);
  const prevExportCascadeRef = useRef(null);
  const geoLoadGenRef = useRef({ country: 0, zone: 0, region: 0, territory: 0 });

  /** Soften geo dropdown loader flash (keep short so Market change does not jerk). */
  const GEO_DROPDOWN_LOAD_MIN_MS = 150;

  // Date range filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangeText, setDateRangeText] = useState("");

  const defaultFY = getDashboardFinancialYearRange();

  // Filter states (aligned with /csrp/dashboard)
  const [filters, setFilters] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
    status: [],
    complaintCategory: "",
    // Multi-select for Business Vertical (same as /csrp/dashboard)
    bv: [],
    invoiceNo: "",
    salesExecutiveName: "",
    market: DEFAULT_ADMIN_DASHBOARD_MARKET,
    country: [],
    zone: [],
    region: [],
    territory: [],
  });

  // Dropdown data states
  const [statusOptions, setStatusOptions] = useState([]);
  const [srCategoryOptions, setSrCategoryOptions] = useState([]);
  const [bvOptions, setBvOptions] = useState([]);
  const [marketOptions, setMarketOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);
  const [geoLoading, setGeoLoading] = useState({
    country: false,
    zone: false,
    region: false,
    territory: false,
  });
  const [invoiceSuggestions, setInvoiceSuggestions] = useState([]);
  const [showInvoiceSuggestions, setShowInvoiceSuggestions] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [zoneNames, setZoneNames] = useState([]);
  const [zonesData, setZonesData] = useState([]); // Store zones with codes for click handler
  const [srsByZoneCounts, setSrsByZoneCounts] = useState([]);
  const [avgTATByZone, setAvgTATByZone] = useState([]);
  const [chartLoading, setChartLoading] = useState(() => createAdminWidgetLoadingState(true));
  const [adminDashboardData, setAdminDashboardData] = useState({
    srData: {
      pendingSRCount: 0,
      approvedSRCount: 0,
      rejectedSRCount: 0,
      closedSRCount: 0,
      queryRaisedSRCount: 0,
      totalSrCount: null,
      slaBreachedSRCount: 0,
      avgApprovalTatDays: null,
    },
    complaintCategoryData: {
      quality: 0,
      dispatch: 0,
      installation: 0,
      commercial: 0,
    },
    srCategoryData: { labels: [], srCounts: [], categoryCodes: [] },
    productVerticalData: { verticals: [], srCounts: [], codes: [] },
    valueSlabData: { slabs: [], srCounts: [] },
    totalApprovedAmountInr: 0,
    totalApprovedAmountUsd: 0,
    avgQueriesPerSr: null,
    slaCompliancePercent: 0,
    srsByCountry: [],
    srsByZone: [],
    repeatComplaintMonthLabels: [],
    repeatComplaintCounts: [],
    settlementTypeLabels: [],
    settlementTypeCounts: [],
    pendingSettlement: [],
    maxQueries: [],
    reopenedSrs: [],
  });

  const [chartCardDates, setChartCardDates] = useState(() =>
    createAdminChartCardDates(defaultFY)
  );
  chartCardDatesRef.current = chartCardDates;

  const [openVsClosedSrData, setOpenVsClosedSrData] = useState({
    pendingSRCount: 0,
    approvedSRCount: 0,
    closedSRCount: 0,
  });

  // Chart collapse state - all charts open by default
  const [chartCollapsed, setChartCollapsed] = useState({
    openVsClosed: false,
    slaCompliance: false,
    srsByCategory: false,
    srsByZone: false,
    avgTATByZone: false,
    srsByProduct: false,
    repeatComplaints: false,
    settlementType: false,
    maxQueries: false,
  });

  // Toggle chart collapse
  const toggleChart = (chartKey) => {
    setChartCollapsed((prev) => ({
      ...prev,
      [chartKey]: !prev[chartKey],
    }));
  };

  useEffect(() => {
    zoneOptionsRef.current = zoneOptions;
  }, [zoneOptions]);

  useEffect(() => {
    regionOptionsRef.current = regionOptions;
  }, [regionOptions]);

  const isExportCascade = useMemo(
    () => isExportMarketSelected(filters.market, marketOptions),
    [filters.market, marketOptions]
  );

  const exportCountryOptions = useMemo(() => {
    if (!isExportCascade || !filters.zone?.length) return [];
    if (isGeoAllFilterValue(filters.zone)) return [GEO_ALL_OPTION];
    const codes = getCountryCodesForSelectedZones(filters.zone, zoneOptions);
    if (!codes.size) return countryOptions;
    return countryOptions.filter(
      (c) => codes.has(c.id) || isGeoAllSelectionToken(c.id)
    );
  }, [isExportCascade, filters.zone, zoneOptions, countryOptions]);

  const pickDefaultMarket = (options) => {
    if (!Array.isArray(options) || !options.length) {
      return DEFAULT_ADMIN_DASHBOARD_MARKET;
    }
    const byCode = options.find(
      (o) => String(o.id || "").trim().toUpperCase() === DEFAULT_ADMIN_DASHBOARD_MARKET
    );
    if (byCode?.id) return byCode.id;
    const byName = options.find(
      (o) => String(o.text || "").trim().toLowerCase() === "domestic"
    );
    if (byName?.id) return byName.id;
    return options[0]?.id || DEFAULT_ADMIN_DASHBOARD_MARKET;
  };

  const runGeoDropdownLoad = async (level, task) => {
    geoLoadGenRef.current[level] += 1;
    const generation = geoLoadGenRef.current[level];
    setGeoLoading((prev) => ({ ...prev, [level]: true }));
    const startedAt = Date.now();
    try {
      await task();
    } finally {
      const elapsed = Date.now() - startedAt;
      const remain = GEO_DROPDOWN_LOAD_MIN_MS - elapsed;
      if (remain > 0) {
        await new Promise((resolve) => setTimeout(resolve, remain));
      }
      if (geoLoadGenRef.current[level] === generation) {
        setGeoLoading((prev) => ({ ...prev, [level]: false }));
      }
    }
  };

  const handleGeoFilterChange = (field, value, optionList = []) => {
    const exportCascade = isExportMarketSelected(filters.market, marketOptions);
    const raw = Array.isArray(value)
      ? value.map((x) => String(x ?? "").trim()).filter(Boolean)
      : [];

    const realOnly = raw.filter((id) => {
      const opt = (optionList || []).find((o) => String(o?.id ?? "").trim() === id);
      return !isGeoAllOptionRow(id, opt?.text);
    });
    const pickedAll = selectionIncludesGeoAll(raw, optionList);
    const prevWasAll = isGeoAllFilterValue(filters[field]);

    // Master-data "All" row → set child geos to All immediately (no option load).
    // If user already had All and adds a real code, switch to that real selection instead.
    if (pickedAll && !(prevWasAll && realOnly.length > 0)) {
      const allVal = [USER_DEFAULT_GEO_CODE];
      if (field === "country") {
        setZoneOptions([GEO_ALL_OPTION]);
        setRegionOptions([GEO_ALL_OPTION]);
        setTerritoryOptions([GEO_ALL_OPTION]);
        setFilters((prev) => ({
          ...prev,
          country: allVal,
          zone: allVal,
          region: allVal,
          territory: allVal,
        }));
        return;
      }
      if (field === "zone") {
        setRegionOptions([GEO_ALL_OPTION]);
        setTerritoryOptions([GEO_ALL_OPTION]);
        if (exportCascade) {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            country: allVal,
            region: allVal,
            territory: allVal,
          }));
        } else {
          setFilters((prev) => ({
            ...prev,
            zone: allVal,
            region: allVal,
            territory: allVal,
          }));
        }
        return;
      }
      if (field === "region") {
        setTerritoryOptions([GEO_ALL_OPTION]);
        setFilters((prev) => ({
          ...prev,
          region: allVal,
          territory: allVal,
        }));
        return;
      }
      if (field === "territory") {
        setFilters((prev) => ({ ...prev, territory: allVal }));
        return;
      }
    }

    // Normal multi-select (or switching away from All to specific codes).
    const selected = realOnly;

    setFilters((prev) => {
      if (field === "country") {
        if (exportCascade) {
          return { ...prev, country: selected, region: [], territory: [] };
        }
        return { ...prev, country: selected, zone: [], region: [], territory: [] };
      }
      if (field === "zone") {
        if (exportCascade) {
          return { ...prev, zone: selected, country: [], region: [], territory: [] };
        }
        return { ...prev, zone: selected, region: [], territory: [] };
      }
      if (field === "region") {
        return { ...prev, region: selected, territory: [] };
      }
      if (field === "territory") {
        return { ...prev, territory: selected };
      }
      return prev;
    });
  };

  // Build a drilldown URL to /admin-service-requests, always carrying the current
  // date range and optionally a status token, category, settlementType, bv, srNo, etc.
  // Per-click overrides for fromDate / toDate are supported (e.g. Repeat Complaints
  // line chart click narrows the window to the clicked month only).
  const buildAdminSRDrilldownQuery = React.useCallback(
    ({
      statusToken,
      category,
      settlementType,
      bv,
      srNo,
      fromDate,
      toDate,
      // Repeat-complaints drilldown: when supplied, the SR list page restricts
      // results to invoices that have multiple distinct SRCodes raised within
      // [repeatScopeFrom..repeatScopeTo]. The visible date range
      // (fromDate/toDate) typically narrows to the clicked month while these
      // remain on the dashboard's full window so the "repeat" universe matches
      // the chart's count.
      repeatScopeFrom,
      repeatScopeTo,
      zoneName,
    } = {}) => {
      const qp = new URLSearchParams();

      // Always pass the active date range (fall back to FY if not set), unless
      // the caller provided explicit fromDate/toDate overrides for this drilldown.
      const fromOverride = fromDate != null && String(fromDate).trim() ? String(fromDate).trim() : null;
      const toOverride = toDate != null && String(toDate).trim() ? String(toDate).trim() : null;
      const from = fromOverride || filters?.dateRangeStart || defaultFY.dateRangeStart;
      const to = toOverride || filters?.dateRangeEnd || defaultFY.dateRangeEnd;
      if (from) qp.set("fromDate", from);
      if (to) qp.set("toDate", to);

      // Business Vertical (CSV of selected BV ids from dashboard filter)
      const bvParam = bv != null
        ? String(bv).trim()
        : (Array.isArray(filters?.bv) ? filters.bv.filter(Boolean).join(",") : "");
      if (bvParam) qp.set("bv", bvParam);

      const marketParam =
        filters?.market != null ? String(filters.market).trim() : "";
      if (marketParam) qp.set("marketCode", marketParam);

      // Drill-down specifics
      if (statusToken != null && String(statusToken).trim()) qp.set("status", String(statusToken).trim());
      if (category != null && String(category).trim()) qp.set("category", String(category).trim());
      if (settlementType != null && String(settlementType).trim()) qp.set("settlementType", String(settlementType).trim());
      if (srNo != null && String(srNo).trim()) qp.set("srNo", String(srNo).trim());
      if (repeatScopeFrom != null && String(repeatScopeFrom).trim())
        qp.set("repeatScopeFrom", String(repeatScopeFrom).trim());
      if (repeatScopeTo != null && String(repeatScopeTo).trim())
        qp.set("repeatScopeTo", String(repeatScopeTo).trim());
      if (repeatScopeFrom != null || repeatScopeTo != null)
        qp.set("repeatComplaint", "1");
      if (zoneName != null && String(zoneName).trim())
        qp.set("zoneName", String(zoneName).trim());

      const qs = qp.toString();
      return qs ? `/admin-service-requests?${qs}` : "/admin-service-requests";
    },
    [filters, defaultFY]
  );

  const navigateAdminDrilldown = React.useCallback(
    (drilldownArgs) => {
      clearAdminServiceRequestListState();
      saveAdminDashboardState({
        filters: { ...filters },
        chartCardDates: { ...chartCardDates },
        chartCollapsed: { ...chartCollapsed },
        filterExpanded,
        startDate,
        endDate,
        dateRangeText,
        pendingRestore: false,
      });
      navigate(buildAdminSRDrilldownQuery(drilldownArgs));
    },
    [
      filters,
      chartCollapsed,
      filterExpanded,
      startDate,
      endDate,
      dateRangeText,
      chartCardDates,
      navigate,
      buildAdminSRDrilldownQuery,
    ]
  );

  const getChartDrilldownDates = React.useCallback((cardKey) => {
    const card = chartCardDatesRef.current?.[cardKey];
    return {
      fromDate: card?.dateRangeStart || filters.dateRangeStart || defaultFY.dateRangeStart,
      toDate: card?.dateRangeEnd || filters.dateRangeEnd || defaultFY.dateRangeEnd,
    };
  }, [filters.dateRangeStart, filters.dateRangeEnd, defaultFY.dateRangeStart, defaultFY.dateRangeEnd]);

  const transformToChartData = useCallback((rows, labelKey, valueKey, opts = {}) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const labels = [];
    const data = [];
    safeRows.forEach((r) => {
      const label = r?.[labelKey];
      const value = r?.[valueKey];
      if (label === null || label === undefined || label === "") return;
      labels.push(String(label));
      data.push(Number(value) || 0);
    });
    if (labels.length === 0) {
      return { labels: [opts.emptyLabel || "–"], data: [0] };
    }
    const take = Number(opts.take) > 0 ? Number(opts.take) : null;
    if (take) {
      return { labels: labels.slice(0, take), data: data.slice(0, take) };
    }
    return { labels, data };
  }, []);

  const hasAdminBarChartData = useCallback((labels, data) => {
    const L = Array.isArray(labels) ? labels : [];
    const D = Array.isArray(data) ? data : [];
    if (L.length === 0) return false;
    if (L.length === 1 && ["–", "-", "N/A", "n/a"].includes(String(L[0]).trim())) {
      return false;
    }
    return D.some((v) => Number(v) > 0);
  }, []);

  const getBarAxisScale = useCallback((values) => {
    const safe = Array.isArray(values) ? values.map((v) => Number(v) || 0) : [];
    const maxVal = safe.length > 0 ? Math.max(...safe) : 0;
    const yMax =
      maxVal === 0
        ? 5
        : Math.ceil(maxVal * 1.2) <= 10
          ? Math.ceil(maxVal * 1.2)
          : Math.ceil((maxVal * 1.2) / 5) * 5;
    const stepSize = yMax <= 10 ? 1 : yMax <= 50 ? 5 : 10;
    return { yMax, stepSize };
  }, []);

  const buildAdminBarDataset = useCallback((labels, data, labelText, colors) => {
    const safeLabels = Array.isArray(labels) ? labels : [];
    const safeData = Array.isArray(data) ? data : [];
    const palette = Array.isArray(colors) && colors.length > 0 ? colors : ["#4a90e2", "#50c878", "#ff9800", "#17a2b8", "#9c27b0"];
    const bg = safeLabels.map((_, i) => palette[i % palette.length]);
    return [
      {
        label: labelText,
        data: safeData,
        backgroundColor: bg,
        borderColor: bg,
        borderWidth: 1,
        barPercentage: 0.7,
        categoryPercentage: safeLabels.length <= 1 ? 0.3 : 0.8,
        barThickness: 40,
      },
    ];
  }, []);

  const wrapTickLabel = (raw, maxLineLen = 14, maxLines = 3) => {
    const text = String(raw ?? "").trim();
    if (!text) return "";
    const parts = text.split(/\s+/).filter(Boolean);
    const lines = [];
    let cur = "";
    for (const p of parts) {
      const next = cur ? `${cur} ${p}` : p;
      if (next.length <= maxLineLen) cur = next;
      else {
        if (cur) lines.push(cur);
        cur = p;
      }
      if (lines.length >= maxLines) break;
    }
    if (lines.length < maxLines && cur) lines.push(cur);
    return lines.length > 1 ? lines : lines[0];
  };

  /** Horizontal SR number ticks — break path-style labels across lines at slashes. */
  const wrapSrNumberTickLabel = (raw) => {
    const text = String(raw ?? "").trim();
    if (!text) return "";
    const slashParts = text.split("/").filter(Boolean);
    if (slashParts.length >= 3) {
      const mid = Math.ceil(slashParts.length / 2);
      const firstLine = `${slashParts.slice(0, mid).join("/")}/`;
      const secondLine = slashParts.slice(mid).join("/");
      return [firstLine, secondLine];
    }
    if (text.length > 20) {
      const slashIdx = text.lastIndexOf("/", Math.ceil(text.length / 2));
      if (slashIdx > 0) {
        return [text.slice(0, slashIdx + 1), text.slice(slashIdx + 1)];
      }
      const mid = Math.ceil(text.length / 2);
      return [text.slice(0, mid), text.slice(mid)];
    }
    return text;
  };

  const buildAdminBarOptions = useCallback((dataValues, onClickHandler, tooltipLabelPrefix = "Count") => {
    const { yMax, stepSize } = getBarAxisScale(dataValues);
    return {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              return `${tooltipLabelPrefix}: ${context.parsed.y}`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          max: yMax,
          suggestedMax: yMax,
          ticks: {
            stepSize,
            precision: 0,
            maxTicksLimit: 11,
          },
          grid: {
            color: "rgba(0, 0, 0, 0.1)",
          },
        },
        x: {
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            padding: 8,
            font: { size: 11, weight: "600" },
            crossAlign: "near",
            callback: function (value) {
              const label =
                typeof this?.getLabelForValue === "function"
                  ? this.getLabelForValue(value)
                  : value;
              return wrapTickLabel(label, 14, 2);
            },
          },
          grid: {
            display: false,
          },
        },
      },
      onClick: onClickHandler,
    };
  }, [getBarAxisScale, wrapTickLabel]);

  // Total SRs must match the same filtered status buckets as the API (Result set 1). Do not prefer
  // totalSrCount from the wire — it can disagree with the reader/column order and stick at "all SRs".
  const totalSRs =
    (adminDashboardData?.srData?.pendingSRCount || 0) +
    (adminDashboardData?.srData?.approvedSRCount || 0) +
    (adminDashboardData?.srData?.rejectedSRCount || 0) +
    (adminDashboardData?.srData?.closedSRCount || 0) +
    (adminDashboardData?.srData?.queryRaisedSRCount || 0);

  // SRs by Zone — API (admin-dashboard); fallback to legacy zone state if empty
  const srsByZoneData = useMemo(() => {
    const z = adminDashboardData?.srsByZone;
    if (Array.isArray(z) && z.length > 0) {
      return {
        labels: z.map((x) => displayInvoiceZoneName(x.zoneName || x.ZoneName)),
        data: z.map((x) => Number(x.srCount ?? x.SrCount ?? x.SRCount) || 0),
      };
    }
    const labels = zoneNames.length > 0 ? zoneNames : [];
    if (labels.length === 0) return { labels: ["–"], data: [0] };
    let data = srsByZoneCounts.length > 0 ? srsByZoneCounts : labels.map(() => 0);
    if (data.length < labels.length) {
      data = [...data, ...Array(labels.length - data.length).fill(0)];
    } else if (data.length > labels.length) {
      data = data.slice(0, labels.length);
    }
    return { labels, data };
  }, [adminDashboardData.srsByZone, zoneNames, srsByZoneCounts]);

  const avgTATByZoneData = useMemo(() => {
    const z = adminDashboardData?.srsByZone;
    if (Array.isArray(z) && z.length > 0) {
      return {
        labels: z.map((x) => displayInvoiceZoneName(x.zoneName || x.ZoneName)),
        data: z.map((x) => Number(x.avgTatDays ?? x.AvgTatDays) || 0),
      };
    }
    const labels = zoneNames.length > 0 ? zoneNames : [];
    if (labels.length === 0) return { labels: ["–"], data: [0] };
    let data = avgTATByZone.length > 0 ? avgTATByZone : labels.map(() => 0);
    if (data.length < labels.length) {
      data = [...data, ...Array(labels.length - data.length).fill(0)];
    } else if (data.length > labels.length) {
      data = data.slice(0, labels.length);
    }
    return { labels, data };
  }, [adminDashboardData.srsByZone, zoneNames, avgTATByZone]);

  const srsByCountryData = useMemo(() => {
    const rows = adminDashboardData?.srsByCountry;
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return rows.map((r) => ({
      country: r.countryName || r.CountryName || "–",
      count: r.srCount ?? r.sRCount ?? r.SRCount ?? 0,
      percentage: Number(r.pct ?? r.Pct ?? 0),
    }));
  }, [adminDashboardData.srsByCountry]);

  // SRs by Category data (dynamic; same shape as /csrp/dashboard: srCategoryData.labels + srCategoryData.srCounts)
  const srsByCategoryData = useMemo(() => {
    const L = adminDashboardData?.srCategoryData?.labels || adminDashboardData?.srCategoryData?.Labels || [];
    const D = adminDashboardData?.srCategoryData?.srCounts || adminDashboardData?.srCategoryData?.SrCounts || [];
    if (Array.isArray(L) && Array.isArray(D) && L.length > 0) {
      return {
        labels: L.map((x) => (x == null || String(x).trim() === "" ? "–" : String(x))),
        data: D.map((n) => Number(n) || 0),
      };
    }
    // Fallback to legacy complaint-category slots (older payloads)
    return {
      labels: ["Quality", "Dispatch", "Installation", "Commercial"],
      data: [
        adminDashboardData?.complaintCategoryData?.quality || 0,
        adminDashboardData?.complaintCategoryData?.dispatch || 0,
        adminDashboardData?.complaintCategoryData?.installation || 0,
        adminDashboardData?.complaintCategoryData?.commercial || 0,
      ],
    };
  }, [adminDashboardData]);

  // SRs by Business Vertical (product vertical API field names unchanged)
  const srsByProductData = {
    labels: adminDashboardData?.productVerticalData?.verticals || [],
    data: adminDashboardData?.productVerticalData?.srCounts || [],
    codes: adminDashboardData?.productVerticalData?.codes || [],
  };

  const repeatComplaintsData = useMemo(() => {
    const L = adminDashboardData?.repeatComplaintMonthLabels || [];
    const D = adminDashboardData?.repeatComplaintCounts || [];
    if (!Array.isArray(L) || L.length === 0) return { labels: ["–"], data: [0] };
    return {
      labels: L,
      data: (D || []).map((n) => Number(n) || 0),
    };
  }, [
    adminDashboardData.repeatComplaintMonthLabels,
    adminDashboardData.repeatComplaintCounts,
  ]);

  const SETTLEMENT_PIE_COLORS = useMemo(
    () => ["#4a90e2", "#50c878", "#ff9800", "#9c27b0", "#e91e63", "#00bcd4", "#795548"],
    []
  );

  const settlementTypeData = useMemo(() => {
    const L = adminDashboardData?.settlementTypeLabels || [];
    const D = adminDashboardData?.settlementTypeCounts || [];
    const labels = Array.isArray(L)
      ? L.map((label) => String(label || "").trim()).filter(Boolean)
      : [];
    const data = labels.map((_, idx) => Number((D || [])[idx]) || 0);
    return { labels, data };
  }, [
    adminDashboardData.settlementTypeLabels,
    adminDashboardData.settlementTypeCounts,
  ]);

  const srsByZoneHasData = useMemo(
    () => hasAdminBarChartData(srsByZoneData.labels, srsByZoneData.data),
    [hasAdminBarChartData, srsByZoneData]
  );

  const avgTATByZoneHasData = useMemo(
    () => hasAdminBarChartData(avgTATByZoneData.labels, avgTATByZoneData.data),
    [hasAdminBarChartData, avgTATByZoneData]
  );

  const settlementTypeHasData = useMemo(
    () => hasAdminBarChartData(settlementTypeData.labels, settlementTypeData.data),
    [hasAdminBarChartData, settlementTypeData]
  );

  const pendingSettlementData = useMemo(() => {
    const rows = adminDashboardData?.pendingSettlement;
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const fmt = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });
    return rows.map((r) => ({
      srNo: r.srNumber || r.sRNumber || r.SRNumber || "–",
      amount: fmt.format(Number(r.amountInr ?? r.AmountInr) || 0),
      daysPending: r.daysPending ?? r.DaysPending ?? 0,
    }));
  }, [adminDashboardData.pendingSettlement]);

  const maxQueriesData = useMemo(() => {
    const rows = adminDashboardData?.maxQueries;
    if (!Array.isArray(rows) || rows.length === 0) {
      return { labels: ["–"], data: [0] };
    }
    return {
      labels: rows.map((r) => r.srNumber || r.sRNumber || r.SRNumber || "–"),
      data: rows.map((r) => Number(r.queryCount ?? r.QueryCount) || 0),
    };
  }, [adminDashboardData.maxQueries]);

  const reopenedSRsData = useMemo(() => {
    const rows = adminDashboardData?.reopenedSrs;
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return rows.map((r) => ({
      srNo: r.srNumber || r.sRNumber || r.SRNumber || "–",
      reopenedDate: r.reopenedDate || r.ReopenedDate || "–",
      reason: r.reason || r.Reason || "–",
    }));
  }, [adminDashboardData.reopenedSrs]);

  // Open vs Closed Donut Chart
  // In admin context, "Open" should include Pending + WIP.
  // Dashboard payload does not expose a dedicated WIP field here; WIP is carried in approvedSRCount bucket.
  const openBucketCount =
    (openVsClosedSrData?.pendingSRCount || 0) +
    (openVsClosedSrData?.approvedSRCount || 0);

  const openVsClosedChartData = {
    labels: ["Open", "Closed"],
    datasets: [
      {
        label: "SR Count",
        data: [
          openBucketCount,
          openVsClosedSrData?.closedSRCount || 0,
        ],
        backgroundColor: ["#ff9800", "#50c878"],
        borderColor: ["#ff9800", "#50c878"],
        borderWidth: 1,
      },
    ],
  };

  const openVsClosedChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "60%",
    onClick: (_event, elements) => {
      if (!elements || elements.length === 0) return;
      const idx = elements[0].index;
      const dates = getChartDrilldownDates("openVsClosed");
      if (idx === 0) {
        navigateAdminDrilldown({ ...dates, statusToken: 2 });
      } else if (idx === 1) {
        navigateAdminDrilldown({ ...dates, statusToken: 5 });
      }
    },
  };

  // SRs by Category Pie Chart
  const categoryChartData = {
    labels: srsByCategoryData.labels,
    datasets: [
      {
        label: "SR Count",
        data: srsByCategoryData.data,
        backgroundColor: ["#4a90e2", "#50c878", "#ff9800", "#17a2b8"],
        borderColor: ["#4a90e2", "#50c878", "#ff9800", "#17a2b8"],
        borderWidth: 1,
      },
    ],
  };

  // Handle category chart click
  const handleCategoryChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElement = elements[0];
      const index = clickedElement.index;
      const label = categoryChartData.labels[index];
      if (label) {
        navigateAdminDrilldown({
          ...getChartDrilldownDates("category"),
          category: String(label),
        });
      }
    }
  };

  const categoryChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    onClick: handleCategoryChartClick,
  };

  // SRs by Business Vertical bar chart
  const productChartData = useMemo(() => {
    return {
      labels: srsByProductData.labels,
      datasets: buildAdminBarDataset(
        srsByProductData.labels,
        srsByProductData.data,
        "SR Count",
        ["#4a90e2", "#50c878", "#ff9800", "#17a2b8", "#9c27b0"]
      ),
    };
  }, [srsByProductData.labels, srsByProductData.data, buildAdminBarDataset]);

  const resolveBusinessVerticalDrilldownCode = useCallback(
    (verticalName, codeFromApi) => {
      const fromApi = codeFromApi != null ? String(codeFromApi).trim() : "";
      if (fromApi) return fromApi;
      const nameKey = String(verticalName ?? "").trim().toLowerCase();
      if (!nameKey) return "";
      const opt = (bvOptions || []).find(
        (o) => String(o?.text ?? "").trim().toLowerCase() === nameKey
      );
      if (opt?.id) return String(opt.id).trim();
      return String(verticalName ?? "").trim();
    },
    [bvOptions]
  );

  // Handle product chart click — filter SR list by Business Vertical (CATGCODE), not SR Category
  const handleProductChartClick = useCallback((event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;
      const productName = srsByProductData.labels[clickedElementIndex];
      const codeFromApi = srsByProductData.codes?.[clickedElementIndex];
      const bvCode = resolveBusinessVerticalDrilldownCode(productName, codeFromApi);
      if (bvCode) {
        navigateAdminDrilldown({ ...getChartDrilldownDates("bv"), bv: bvCode });
      }
    }
  }, [srsByProductData, navigateAdminDrilldown, resolveBusinessVerticalDrilldownCode, getChartDrilldownDates]);

  const productChartOptions = useMemo(() => {
    return buildAdminBarOptions(srsByProductData.data, handleProductChartClick, "Count");
  }, [srsByProductData.data, handleProductChartClick, buildAdminBarOptions]);

  // SRs by Zone Bar Chart (memoized to update when zone data changes)
  const zoneChartData = useMemo(() => {
    return {
      labels: srsByZoneData.labels,
      datasets: buildAdminBarDataset(
        srsByZoneData.labels,
        srsByZoneData.data,
        "SR Count",
        ["#4a90e2", "#50c878", "#ff9800", "#17a2b8", "#9c27b0"]
      ),
    };
  }, [srsByZoneData, buildAdminBarDataset]);

  // Handle zone chart click
  const handleZoneChartClick = useCallback((event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;
      const zoneName = srsByZoneData.labels[clickedElementIndex];
      if (zoneName) {
        navigateAdminDrilldown({
          ...getChartDrilldownDates("zone"),
          zoneName,
        });
      }
    }
  }, [srsByZoneData, navigateAdminDrilldown, getChartDrilldownDates]);

  const zoneChartOptions = useMemo(() => {
    return buildAdminBarOptions(srsByZoneData.data, handleZoneChartClick, "Count");
  }, [srsByZoneData.data, handleZoneChartClick, buildAdminBarOptions]);

  // Avg TAT by Zone Bar Chart (memoized to update when zone data changes)
  const avgTATByZoneChartData = useMemo(() => {
    return {
      labels: avgTATByZoneData.labels,
      datasets: buildAdminBarDataset(
        avgTATByZoneData.labels,
        avgTATByZoneData.data,
        "Avg TAT (days)",
        ["#4a90e2", "#50c878", "#ff9800", "#17a2b8", "#9c27b0", "#00bcd4", "#8bc34a"]
      ),
    };
  }, [avgTATByZoneData, buildAdminBarDataset]);

  // Handle Avg TAT by Zone chart click
  const handleAvgTATByZoneChartClick = useCallback((event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;
      const zoneName = avgTATByZoneData.labels[clickedElementIndex];
      if (zoneName) {
        navigateAdminDrilldown({
          ...getChartDrilldownDates("zone"),
          zoneName,
        });
      }
    }
  }, [avgTATByZoneData, navigateAdminDrilldown, getChartDrilldownDates]);

  const avgTATByZoneChartOptions = useMemo(() => {
    const base = buildAdminBarOptions(
      avgTATByZoneData.data,
      handleAvgTATByZoneChartClick,
      "Avg TAT (days)"
    );
    return {
      ...base,
      plugins: {
        ...base.plugins,
        tooltip: {
          callbacks: {
            label: function (context) {
              const days = Number(context?.parsed?.y) || 0;
              const zone = avgTATByZoneData.labels[context.dataIndex];
              const srIdx = srsByZoneData.labels.indexOf(zone);
              const srCount = srIdx >= 0 ? Number(srsByZoneData.data[srIdx]) || 0 : null;
              const dayText = `Avg TAT: ${days} day${days === 1 ? "" : "s"}`;
              if (srCount == null) return dayText;
              return `${dayText} (${srCount} SR${srCount === 1 ? "" : "s"})`;
            },
          },
        },
      },
      scales: {
        ...base.scales,
        y: {
          ...base.scales.y,
          title: {
            display: true,
            text: "Days",
            font: { size: 11, weight: "600" },
          },
        },
      },
    };
  }, [
    avgTATByZoneData.data,
    avgTATByZoneData.labels,
    srsByZoneData.labels,
    srsByZoneData.data,
    handleAvgTATByZoneChartClick,
    buildAdminBarOptions,
  ]);

  // Repeat Complaints Line Chart
  const repeatComplaintsChartData = {
    labels: repeatComplaintsData.labels,
    datasets: [
      {
        label: "Repeat Complaints",
        data: repeatComplaintsData.data,
        borderColor: "#dc3545",
        backgroundColor: "rgba(220, 53, 69, 0.1)",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointBackgroundColor: "#dc3545",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },
    ],
  };

  // Handle Repeat Complaints line chart click. Each label corresponds to one of
  // the rolling 6 months returned by the SP (window starts at @FromDate).
  // Clicking month index N narrows the SR list's visible date range to that
  // month, while ALSO carrying the dashboard's full window as the
  // "repeat scope" so the SR list SP only includes invoices that have multiple
  // distinct SRCodes raised within that wider window — matching the chart's
  // definition of "repeat" exactly (Result 7 of usp_GetAdminDashboardData).
  const handleRepeatComplaintsChartClick = useCallback((event, elements) => {
    if (!elements || elements.length === 0) return;
    const idx = Number(elements[0].index ?? -1);
    if (!Number.isFinite(idx) || idx < 0) return;

    const baseFrom =
      chartCardDatesRef.current?.repeat?.dateRangeStart ||
      filters?.dateRangeStart ||
      defaultFY.dateRangeStart;
    let baseDate;
    if (baseFrom && /^\d{4}-\d{2}-\d{2}$/.test(String(baseFrom).trim())) {
      const [yy, mm, dd] = String(baseFrom).trim().split("-").map((n) => Number(n));
      baseDate = new Date(yy, (mm || 1) - 1, dd || 1);
    } else {
      const now = new Date();
      baseDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    const targetYear = baseDate.getFullYear();
    const targetMonthIdx = baseDate.getMonth() + idx;
    const monthStart = new Date(targetYear, targetMonthIdx, 1);
    const monthEnd = new Date(targetYear, targetMonthIdx + 1, 0); // last day of target month

    const pad = (n) => String(n).padStart(2, "0");
    const fromIso = `${monthStart.getFullYear()}-${pad(monthStart.getMonth() + 1)}-01`;
    const toIso = `${monthEnd.getFullYear()}-${pad(monthEnd.getMonth() + 1)}-${pad(monthEnd.getDate())}`;

    // Dashboard's full window — used by the SR list SP to determine which
    // invoices count as "repeat" (2+ distinct SRCodes).
    const scopeFrom =
      chartCardDatesRef.current?.repeat?.dateRangeStart ||
      filters?.dateRangeStart ||
      defaultFY.dateRangeStart;
    const scopeTo =
      chartCardDatesRef.current?.repeat?.dateRangeEnd ||
      filters?.dateRangeEnd ||
      defaultFY.dateRangeEnd;

    navigateAdminDrilldown({
      fromDate: fromIso,
      toDate: toIso,
      repeatScopeFrom: scopeFrom,
      repeatScopeTo: scopeTo,
    });
  }, [filters, defaultFY, navigateAdminDrilldown]);

  const repeatComplaintsChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    /* Line + fill: default interaction often yields no active elements on click
       (user clicks shaded area, not the stroke). Use index mode + fallback below. */
    interaction: {
      mode: "index",
      intersect: false,
      axis: "x",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `Count: ${context.parsed.y}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    onHover: (event, elements, chart) => {
      const target = event?.native?.target;
      if (!target) return;
      const ch =
        chart ||
        (target instanceof HTMLCanvasElement ? ChartJS.getChart(target) : null);
      let els = elements;
      if (!els?.length && ch?.getElementsAtEventForMode) {
        els = ch.getElementsAtEventForMode(event, "index", { intersect: false }, true);
      }
      target.style.cursor = els && els.length > 0 ? "pointer" : "default";
    },
    onClick: (event, elements) => {
      const canvas = event?.native?.target;
      const chart =
        canvas instanceof HTMLCanvasElement ? ChartJS.getChart(canvas) : null;
      let els = elements;
      if ((!els || els.length === 0) && chart?.getElementsAtEventForMode) {
        els = chart.getElementsAtEventForMode(
          event,
          "index",
          { intersect: false },
          true
        );
      }
      handleRepeatComplaintsChartClick(event, els || []);
    },
  }), [handleRepeatComplaintsChartClick]);

  // Settlement Type Split Pie Chart
  const settlementTypeChartData = useMemo(() => ({
    labels: settlementTypeData.labels,
    datasets: [
      {
        label: "Count",
        data: settlementTypeData.data,
        backgroundColor: settlementTypeData.labels.map(
          (_, index) => SETTLEMENT_PIE_COLORS[index % SETTLEMENT_PIE_COLORS.length]
        ),
        borderColor: settlementTypeData.labels.map(
          (_, index) => SETTLEMENT_PIE_COLORS[index % SETTLEMENT_PIE_COLORS.length]
        ),
        borderWidth: 1,
      },
    ],
  }), [settlementTypeData, SETTLEMENT_PIE_COLORS]);

  // Handle settlement type chart click
  const handleSettlementTypeChartClick = useCallback((event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElement = elements[0];
      const index = clickedElement.index;
      const label = settlementTypeChartData.labels[index];
      
      if (label) {
        navigateAdminDrilldown({
          ...getChartDrilldownDates("settlement"),
          settlementType: String(label),
        });
      }
    }
  }, [settlementTypeChartData, navigateAdminDrilldown, getChartDrilldownDates]);

  const settlementTypeChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false, // Hide default legend, using custom legend below
        },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    onClick: handleSettlementTypeChartClick, // Add click handler
  }), [handleSettlementTypeChartClick]);

  // SRs with Max Queries Bar Chart
  const maxQueriesChartData = useMemo(() => {
    return {
      labels: maxQueriesData.labels,
      datasets: buildAdminBarDataset(
        maxQueriesData.labels,
        maxQueriesData.data,
        "Query Count",
        ["#dc3545", "#ff9800", "#ffc107", "#4a90e2", "#50c878"]
      ),
    };
  }, [maxQueriesData.labels, maxQueriesData.data, buildAdminBarDataset]);

  // Handle max queries chart click
  const handleMaxQueriesChartClick = useCallback((event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElementIndex = elements[0].index;
      
      // Get the SR number from the clicked bar
      const srNumber = maxQueriesData.labels[clickedElementIndex];
      if (srNumber) {
        navigateAdminDrilldown({ ...getChartDrilldownDates("maxQueries"), srNo: srNumber });
      }
    }
  }, [maxQueriesData, navigateAdminDrilldown, getChartDrilldownDates]);

  const maxQueriesChartOptions = useMemo(() => {
    const base = buildAdminBarOptions(
      maxQueriesData.data,
      handleMaxQueriesChartClick,
      "Count"
    );
    return {
      ...base,
      layout: {
        padding: {
          bottom: 28,
        },
      },
      scales: {
        ...base.scales,
        x: {
          ...base.scales.x,
          ticks: {
            ...base.scales.x.ticks,
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            align: "center",
            crossAlign: "near",
            padding: 8,
            font: { size: 10, weight: "600" },
            callback: function (value) {
              const label =
                typeof this?.getLabelForValue === "function"
                  ? this.getLabelForValue(value)
                  : value;
              return wrapSrNumberTickLabel(label);
            },
          },
        },
      },
    };
  }, [maxQueriesData.data, handleMaxQueriesChartClick, buildAdminBarOptions, wrapSrNumberTickLabel]);

  // Load Status options from SRSTATUSMASTER (same as /csrp/dashboard)
  const loadStatusOptions = async () => {
    try {
      const response = await adminDashboardAPI.getSRStatuses(true);
      const ok = response?.success === true || response?.Success === true;
      const list = response?.data ?? response?.Data;
      if (ok && Array.isArray(list)) {
        const options = list.map((s) => ({
          id: s.srStatusCode || s.SRStatusCode || "",
          text: s.name || s.Name || s.srStatusCode || s.SRStatusCode || "",
        }));
        setStatusOptions(options);
      }
    } catch (e) {
      console.error("Error loading status options:", e);
    }
  };

  // Load SR Category options from Greenlam.SRTYPES for the selected market.
  // Dropdown value uses ISNULL(NAME, SRTypeCode) so it matches usp_GetAdminDashboardData @ComplaintCategory.
  const loadSrCategoryOptions = async (marketCode) => {
    try {
      const code = marketCode != null ? String(marketCode).trim() : "";
      if (!code || code === "0000") {
        setSrCategoryOptions([]);
        return;
      }
      const response = await masterDataAPI.getSRTypes(code, true);
      const payload = response?.data ?? response?.Data ?? response;
      const list =
        (Array.isArray(payload) ? payload : null) ||
        (Array.isArray(payload?.data) ? payload.data : null) ||
        (Array.isArray(payload?.srTypes) ? payload.srTypes : null) ||
        [];
      const seen = new Set();
      const options = [];
      (Array.isArray(list) ? list : []).forEach((x) => {
        const srTypeCode = String(
          x.srTypeCode ?? x.SRTypeCode ?? x.SRTYPECODE ?? x.value ?? x.Value ?? ""
        ).trim();
        const name = String(x.name ?? x.Name ?? x.NAME ?? x.text ?? x.Text ?? "").trim();
        const listLabel = (name || srTypeCode).trim();
        if (!listLabel) return;
        const dedupeKey = listLabel.toLowerCase();
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        options.push({ id: listLabel, text: listLabel, srTypeCode });
      });
      setSrCategoryOptions(options);
    } catch (e) {
      console.error("Error loading SR category options:", e);
      setSrCategoryOptions([]);
    }
  };

  // Load Business Vertical options from CATEGORYMASTER (scoped to selected market)
  const loadBVOptions = async (marketCode) => {
    try {
      const response = await adminDashboardAPI.getProductVerticalOptions({
        isActive: true,
        marketCode: marketCode || undefined,
        pageNumber: 1,
        pageSize: 500,
      });
      if (response?.success && response.data?.categories) {
        const list = response.data.categories || response.data.Categories || [];
        const options = list.map((c) => ({
          id: c.catgCode || c.CatgCode,
          text: c.catgName || c.CatgName || c.catgCode || c.CatgCode,
        }));
        setBvOptions(options);
      }
    } catch (e) {
      console.error("Error loading BV options:", e);
    }
  };

  const loadMarketOptions = async () => {
    try {
      const response = await masterDataAPI.getMarkets("", 1, 500);
      const { ok, payload } = parseApiEnvelope(response);
      const markets = payload?.markets || payload?.Markets || payload || [];
      const options = (Array.isArray(markets) ? markets : [])
        .filter((m) => m.isActive !== false && m.IsActive !== false)
        .map((m) => ({
          id: m.marketCode || m.MARKETCODE || m.MarketCode,
          text: m.marketName || m.MARKETNAME || m.MarketName || m.marketCode,
        }))
        .filter((o) => o.id);
      setMarketOptions(options);

      let nextMarket = "";
      setFilters((prev) => {
        nextMarket =
          prev.market && String(prev.market).trim()
            ? prev.market
            : pickDefaultMarket(options);
        if (prev.market === nextMarket) return prev;
        return { ...prev, market: nextMarket };
      });
      if (!nextMarket) nextMarket = pickDefaultMarket(options);
      return nextMarket;
    } catch (e) {
      console.error("Error loading Market options:", e);
      return "";
    }
  };

  const loadCountryOptions = async () => {
    await runGeoDropdownLoad("country", async () => {
      try {
        const response = await masterDataAPI.getCountries();
        const { ok, payload } = parseApiEnvelope(response);
        const raw = ok && Array.isArray(payload) ? payload : [];
        const byId = new Map();
        raw.forEach((country) => {
          const id = String(country.countryCode ?? country.CountryCode ?? "").trim();
          const text = String(country.countryName ?? country.CountryName ?? "").trim();
          if (!id) return;
          if (isGeoAllOptionRow(id, text)) {
            byId.set(USER_DEFAULT_GEO_CODE, GEO_ALL_OPTION);
            return;
          }
          byId.set(id, { id, text: text || id });
        });
        if (!byId.has(USER_DEFAULT_GEO_CODE)) {
          byId.set(USER_DEFAULT_GEO_CODE, GEO_ALL_OPTION);
        }
        setCountryOptions([...byId.values()]);
      } catch (e) {
        console.error("Error loading Country options:", e);
        setCountryOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadZoneOptions = async (countryCodes) => {
    await runGeoDropdownLoad("zone", async () => {
      try {
        const merged = [];
        const codes = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!codes.length) {
          const res = await territoryMasterAPI.getZones();
          const raw = extractZonesFromResponse(res);
          const active = Array.isArray(raw)
            ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
            : [];
          active.forEach((z) => merged.push(normalizeZoneRow(z, z.countryCode || z.CountryCode)));
        } else {
          const zoneResults = await Promise.all(
            codes.map(async (cc) => {
              const res = await territoryMasterAPI.getZones(cc);
              const raw = extractZonesFromResponse(res);
              const active = Array.isArray(raw)
                ? raw.filter((z) => z.isActive !== false && z.IsActive !== false)
                : [];
              return active.map((z) => normalizeZoneRow(z, cc));
            })
          );
          zoneResults.flat().forEach((z) => merged.push(z));
        }
        const activeRows = merged.filter(
          (z) => z.zoneCode && !isGeoAllOptionRow(z.zoneCode, z.zoneName)
        );
        // Export: one row per zone name (AFRICA once); Domestic: one row per zone code.
        const mapped = isExportCascade
          ? mapExportZonesToOptions(activeRows, countryOptions)
          : (() => {
              const byZone = new Map();
              activeRows.forEach((z) => {
                byZone.set(z.zoneCode, mapZoneToOption(z, countryOptions));
              });
              return [...byZone.values()];
            })();
        setZoneOptions([GEO_ALL_OPTION, ...mapped]);
      } catch (e) {
        console.error("Error loading Zone options:", e);
        setZoneOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadRegionOptions = async (zoneCodes, countryCodes, zonesList, exportMode) => {
    await runGeoDropdownLoad("region", async () => {
      try {
        const zSel = Array.isArray(zoneCodes)
          ? zoneCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        const cSel = Array.isArray(countryCodes)
          ? countryCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!zSel.length || (exportMode && !cSel.length)) {
          regionOptionsRef.current = [GEO_ALL_OPTION];
          setRegionOptions([GEO_ALL_OPTION]);
          return;
        }
        const regionJobs = [];
        for (const zoneOptionId of zSel) {
          const zrow = (zonesList || []).find((x) => x.id === zoneOptionId);
          const linkedCountries = Array.isArray(zrow?.countryCodes)
            ? zrow.countryCodes
            : zrow?.countryCode
              ? [zrow.countryCode]
              : [];
          const countriesToQuery = exportMode
            ? cSel.filter(
                (cc) => !linkedCountries.length || linkedCountries.includes(cc)
              )
            : linkedCountries.length === 1
              ? [linkedCountries[0]]
              : linkedCountries.length
                ? linkedCountries
                : cSel;
          for (const cc of countriesToQuery) {
            if (!cc || isGeoAllSelectionToken(cc)) continue;
            const zoneCode = exportMode
              ? resolveZoneCodeForCountry(zoneOptionId, cc, zonesList)
              : zoneOptionId;
            if (!zoneCode) continue;
            regionJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getRegions(cc, zoneCode);
                const raw = extractRegionsFromResponse(res);
                const active = Array.isArray(raw)
                  ? raw.filter((r) => r.isActive !== false && r.IsActive !== false)
                  : [];
                return active;
              })()
            );
          }
        }
        const regionResults = await Promise.all(regionJobs);
        const merged = [];
        regionResults.forEach((active) => {
          active.forEach((r) => merged.push(normalizeRegionRow(r)));
        });
        const byReg = new Map();
        merged.forEach((r) => {
          if (!r.regionCode || isGeoAllOptionRow(r.regionCode, r.regionName)) return;
          byReg.set(r.regionCode, mapRegionToOption(r));
        });
        const options = [GEO_ALL_OPTION, ...byReg.values()];
        regionOptionsRef.current = options;
        setRegionOptions(options);
      } catch (e) {
        console.error("Error loading Region options:", e);
        regionOptionsRef.current = [GEO_ALL_OPTION];
        setRegionOptions([GEO_ALL_OPTION]);
      }
    });
  };

  const loadTerritoryOptions = async (
    regionCodes,
    zonesList,
    regionsList,
    filterZoneCodes,
    filterCountryCodes
  ) => {
    await runGeoDropdownLoad("territory", async () => {
      try {
        const rSel = Array.isArray(regionCodes)
          ? regionCodes.filter((c) => c && !isGeoAllSelectionToken(c))
          : [];
        if (!rSel.length) {
          setTerritoryOptions([GEO_ALL_OPTION]);
          return;
        }

        const regionsListResolved =
          (Array.isArray(regionsList) && regionsList.length
            ? regionsList
            : regionOptionsRef.current) || [];
        const zonesListResolved =
          (Array.isArray(zonesList) && zonesList.length
            ? zonesList
            : zoneOptionsRef.current) || [];

        const territoryJobs = [];
        for (const rc of rSel) {
          const row = regionsListResolved.find((x) => x.id === rc);
          const pairs = resolveRegionGeoCandidates(
            rc,
            row,
            regionsListResolved,
            zonesListResolved,
            filterZoneCodes,
            filterCountryCodes
          );
          if (!pairs.length) continue;

          for (const { cc, zc } of pairs) {
            if (isGeoAllSelectionToken(cc) || isGeoAllSelectionToken(zc)) continue;
            territoryJobs.push(
              (async () => {
                const res = await territoryMasterAPI.getPaged({
                  countryCode: cc,
                  zoneCode: zc,
                  regionCode: rc,
                  status: "active",
                  pageNumber: 1,
                  pageSize: 5000,
                });
                const { ok, payload } = parseApiEnvelope(res);
                const items = ok && payload
                  ? payload.territories || payload.Territories || []
                  : [];
                return Array.isArray(items)
                  ? items.filter((t) => t.isActive !== false && t.IsActive !== false)
                  : [];
              })()
            );
          }
        }
        const territoryResults = await Promise.all(territoryJobs);
        const merged = [];
        territoryResults.forEach((active) => {
          active.forEach((t) => merged.push(normalizeTerritoryRow(t)));
        });
        const byT = new Map();
        merged.forEach((t) => {
          if (!t.territoryCode || isGeoAllOptionRow(t.territoryCode, t.territoryName)) {
            return;
          }
          byT.set(t.territoryCode, mapTerritoryToOption(t));
        });
        setTerritoryOptions([GEO_ALL_OPTION, ...byT.values()]);
      } catch (e) {
        console.error("Error loading Territory options:", e);
        setTerritoryOptions([GEO_ALL_OPTION]);
      }
    });
  };

  // Load invoice suggestions (dynamic)
  const loadInvoiceSuggestions = async (searchText) => {
    if (searchText.length < 4) {
      setInvoiceSuggestions([]);
      setShowInvoiceSuggestions(false);
      return;
    }
    try {
      const resp = await complaintAPI.getServiceRequestList({
        fromDate: filters.dateRangeStart || undefined,
        toDate: filters.dateRangeEnd || undefined,
        searchText,
        pageNumber: 1,
        pageSize: 20,
      });

      const items = resp?.success ? resp?.data?.items : [];
      const unique = new Set();
      (items || []).forEach((x) => {
        const inv =
          x?.invoiceNumber ||
          x?.InvoiceNumber ||
          x?.billingInvoiceNo ||
          x?.BillingInvoiceNo ||
          "";
        const v = String(inv || "").trim();
        if (!v) return;
        if (v.startsWith(searchText)) unique.add(v);
      });

      setInvoiceSuggestions(Array.from(unique).slice(0, 10));
      setShowInvoiceSuggestions(unique.size > 0);
    } catch (e) {
      console.error("Error loading invoice suggestions:", e);
      setInvoiceSuggestions([]);
      setShowInvoiceSuggestions(false);
    }
  };

  // Handle invoice input change
  const handleInvoiceInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only digits
    setFilters((prev) => ({ ...prev, invoiceNo: value }));
    if (value.length >= 4) {
      loadInvoiceSuggestions(value);
    } else {
      setShowInvoiceSuggestions(false);
    }
  };

  // Handle invoice suggestion click
  const handleInvoiceSuggestionClick = (suggestion) => {
    setFilters((prev) => ({ ...prev, invoiceNo: suggestion }));
    setShowInvoiceSuggestions(false);
  };

  // Close invoice suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showInvoiceSuggestions &&
        !event.target.closest(".invoice-suggestions-list") &&
        !event.target.closest('input[placeholder="Enter invoice number"]')
      ) {
        setShowInvoiceSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInvoiceSuggestions]);

  // Load dashboard data from API – mirrors /csrp/approver-dashboard per-widget date filters
  const buildAdminDashboardParams = React.useCallback(
    (overrideParams = {}, dateOverride = null) => {
      const params = {};
      const fromDate =
        overrideParams.fromDate !== undefined
          ? overrideParams.fromDate
          : dateOverride?.start !== undefined
            ? dateOverride.start
            : filters.dateRangeStart;
      const toDate =
        overrideParams.toDate !== undefined
          ? overrideParams.toDate
          : dateOverride?.end !== undefined
            ? dateOverride.end
            : filters.dateRangeEnd;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const statusList =
        overrideParams.status !== undefined ? overrideParams.status : filters.status;
      const bvList = overrideParams.bv !== undefined ? overrideParams.bv : filters.bv;
      const countryVal =
        overrideParams.country !== undefined ? overrideParams.country : filters.country;
      const zoneVal = overrideParams.zone !== undefined ? overrideParams.zone : filters.zone;
      const regionVal =
        overrideParams.region !== undefined ? overrideParams.region : filters.region;
      const territoryVal =
        overrideParams.territory !== undefined
          ? overrideParams.territory
          : filters.territory;
      const invoiceVal =
        overrideParams.invoiceNo !== undefined
          ? overrideParams.invoiceNo
          : filters.invoiceNo;
      const seNameVal =
        overrideParams.salesExecutiveName !== undefined
          ? overrideParams.salesExecutiveName
          : filters.salesExecutiveName;
      const complaintCatVal =
        overrideParams.complaintCategory !== undefined
          ? overrideParams.complaintCategory
          : filters.complaintCategory;
      const marketVal =
        overrideParams.market !== undefined ? overrideParams.market : filters.market;

      if (invoiceVal && String(invoiceVal).trim()) {
        params.invoiceNumber = String(invoiceVal).trim();
      }
      if (Array.isArray(statusList) && statusList.length > 0) {
        const joinedStatus = statusList.filter(Boolean).join(",");
        if (joinedStatus) params.statusCode = joinedStatus;
      }
      if (Array.isArray(bvList) && bvList.length > 0) {
        const joinedBv = bvList.filter(Boolean).join(",");
        if (joinedBv) params.productVerticalCode = joinedBv;
      }
      const countryCsv = joinGeoFilterForApi(countryVal);
      const zoneExpanded =
        isExportCascade && Array.isArray(zoneVal) && zoneVal.length
          ? expandZoneFilterCodes(zoneVal, zoneOptionsRef.current)
          : zoneVal;
      const zoneCsv = joinGeoFilterForApi(zoneExpanded);
      const regionCsv = joinGeoFilterForApi(regionVal);
      const territoryCsv = joinGeoFilterForApi(territoryVal);
      if (countryCsv) params.countryCode = countryCsv;
      if (zoneCsv) params.zoneCode = zoneCsv;
      if (regionCsv) params.regionCode = regionCsv;
      if (territoryCsv) params.territoryCode = territoryCsv;
      if (seNameVal && seNameVal.trim()) {
        params.salesExecutiveName = seNameVal.trim();
      }
      const complaintCatTrim =
        complaintCatVal != null ? String(complaintCatVal).trim() : "";
      if (complaintCatTrim) params.complaintCategory = complaintCatTrim;
      const marketTrim = marketVal != null ? String(marketVal).trim() : "";
      if (marketTrim) params.marketCode = marketTrim;
      return params;
    },
    [
      filters.dateRangeStart,
      filters.dateRangeEnd,
      filters.status,
      filters.bv,
      filters.country,
      filters.zone,
      filters.region,
      filters.territory,
      filters.invoiceNo,
      filters.salesExecutiveName,
      filters.complaintCategory,
      filters.market,
      isExportCascade,
    ]
  );

  const fetchAdminDashboardPayload = React.useCallback(
    async (overrideParams = {}, dateOverride = null) => {
      try {
        const params = buildAdminDashboardParams(overrideParams, dateOverride);
        const response = await adminDashboardAPI.getAdminDashboardData(params);
        return getAdminDashboardDataPayload(response);
      } catch (e) {
        console.error("Error loading admin dashboard data:", e);
        return null;
      }
    },
    [buildAdminDashboardParams]
  );

  const getCardDateRange = React.useCallback(
    (cardKey, overrideParams = {}) => ({
      start:
        overrideParams.fromDate !== undefined
          ? overrideParams.fromDate
          : chartCardDates[cardKey]?.dateRangeStart,
      end:
        overrideParams.toDate !== undefined
          ? overrideParams.toDate
          : chartCardDates[cardKey]?.dateRangeEnd,
    }),
    [chartCardDates]
  );

  const runChartLoader = React.useCallback(async (widgetKey, task) => {
    setChartLoading((prev) => ({ ...prev, [widgetKey]: true }));
    try {
      await task();
    } finally {
      setChartLoading((prev) => ({ ...prev, [widgetKey]: false }));
    }
  }, []);

  const applyOpenVsClosedFromPayload = (d) => {
    if (!d?.srData) return;
    setOpenVsClosedSrData({
      pendingSRCount: d.srData.pendingSRCount || 0,
      approvedSRCount: d.srData.approvedSRCount || 0,
      closedSRCount: d.srData.closedSRCount || 0,
    });
  };

  const buildKpiSrDataFromPayload = (d) => ({
    pendingSRCount: d?.srData?.pendingSRCount || 0,
    approvedSRCount: d?.srData?.approvedSRCount || 0,
    rejectedSRCount: d?.srData?.rejectedSRCount || 0,
    closedSRCount: d?.srData?.closedSRCount || 0,
    queryRaisedSRCount: d?.srData?.queryRaisedSRCount || 0,
    totalSrCount:
      d?.srData?.totalSrCount !== undefined && d?.srData?.totalSrCount !== null
        ? d.srData.totalSrCount
        : d?.srData?.TotalSrCount !== undefined && d?.srData?.TotalSrCount !== null
          ? d.srData.TotalSrCount
          : null,
    slaBreachedSRCount: d?.srData?.slaBreachedSRCount || 0,
    avgApprovalTatDays: (() => {
      const fromSr = d?.srData?.avgApprovalTatDays ?? d?.srData?.AvgApprovalTatDays;
      const fromRoot = d?.overallAvgTatDays ?? d?.OverallAvgTatDays;
      const val = fromSr ?? fromRoot;
      return val === null || val === undefined || val === "" ? null : val;
    })(),
  });

  /** Apply full admin-dashboard payload to KPI + all charts in one state update. */
  const applyFullAdminDashboardPayload = (d) => {
    if (!d) return;
    applyOpenVsClosedFromPayload(d);
    setAdminDashboardData((prev) => ({
      ...prev,
      srData: buildKpiSrDataFromPayload(d),
      totalApprovedAmountInr:
        Number(d?.totalApprovedAmountInr ?? d?.TotalApprovedAmountInr) || 0,
      totalApprovedAmountUsd:
        Number(d?.totalApprovedAmountUsd ?? d?.TotalApprovedAmountUsd) || 0,
      avgQueriesPerSr:
        d?.avgQueriesPerSr === null ||
        d?.avgQueriesPerSr === undefined ||
        d?.avgQueriesPerSr === ""
          ? null
          : d?.avgQueriesPerSr,
      slaCompliancePercent:
        Number(d?.slaCompliancePercent ?? d?.SlaCompliancePercent) || 0,
      complaintCategoryData: {
        quality: d?.complaintCategoryData?.quality || 0,
        dispatch: d?.complaintCategoryData?.dispatch || 0,
        installation: d?.complaintCategoryData?.installation || 0,
        commercial: d?.complaintCategoryData?.commercial || 0,
      },
      srCategoryData: {
        labels: d?.srCategoryData?.labels || d?.srCategoryData?.Labels || [],
        srCounts: d?.srCategoryData?.srCounts || d?.srCategoryData?.SrCounts || [],
        categoryCodes:
          d?.srCategoryData?.categoryCodes || d?.srCategoryData?.CategoryCodes || [],
      },
      srsByZone: Array.isArray(d?.srsByZone ?? d?.SrsByZone)
        ? (d.srsByZone ?? d.SrsByZone)
        : [],
      productVerticalData: {
        verticals:
          d?.productVerticalData?.verticals || d?.productVerticalData?.Verticals || [],
        srCounts:
          d?.productVerticalData?.srCounts || d?.productVerticalData?.SrCounts || [],
        codes: d?.productVerticalData?.codes || d?.productVerticalData?.Codes || [],
      },
      repeatComplaintMonthLabels: Array.isArray(
        d?.repeatComplaintMonthLabels ?? d?.RepeatComplaintMonthLabels
      )
        ? (d.repeatComplaintMonthLabels ?? d.RepeatComplaintMonthLabels)
        : [],
      repeatComplaintCounts: Array.isArray(
        d?.repeatComplaintCounts ?? d?.RepeatComplaintCounts
      )
        ? (d.repeatComplaintCounts ?? d.RepeatComplaintCounts)
        : [],
      settlementTypeLabels: Array.isArray(
        d?.settlementTypeLabels ?? d?.SettlementTypeLabels
      )
        ? (d.settlementTypeLabels ?? d.SettlementTypeLabels)
        : [],
      settlementTypeCounts: Array.isArray(
        d?.settlementTypeCounts ?? d?.SettlementTypeCounts
      )
        ? (d.settlementTypeCounts ?? d.SettlementTypeCounts)
        : [],
      maxQueries: Array.isArray(d?.maxQueries) ? d.maxQueries : [],
      srsByCountry: Array.isArray(d?.srsByCountry ?? d?.SrsByCountry)
        ? (d.srsByCountry ?? d.SrsByCountry)
        : [],
      pendingSettlement: Array.isArray(d?.pendingSettlement) ? d.pendingSettlement : [],
      reopenedSrs: Array.isArray(d?.reopenedSrs) ? d.reopenedSrs : [],
    }));
  };

  /** One API call feeds KPI cards + all charts when they share the same filters/dates. */
  const loadAllDashboardData = React.useCallback(
    async (overrideParams = {}) => {
      setChartLoading(createAdminWidgetLoadingState(true));
      try {
        const fromDate =
          overrideParams.fromDate !== undefined
            ? overrideParams.fromDate
            : filters.dateRangeStart;
        const toDate =
          overrideParams.toDate !== undefined ? overrideParams.toDate : filters.dateRangeEnd;
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        applyFullAdminDashboardPayload(d);
      } finally {
        setChartLoading(createAdminWidgetLoadingState(false));
      }
    },
    [fetchAdminDashboardPayload, filters.dateRangeStart, filters.dateRangeEnd]
  );

  const loadOpenVsClosedChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("openVsClosed", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("openVsClosed", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        applyOpenVsClosedFromPayload(d);
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadSlaChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("sla", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("sla", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            slaCompliancePercent:
              Number(d?.slaCompliancePercent ?? d?.SlaCompliancePercent) || 0,
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadCategoryChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("category", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("category", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            complaintCategoryData: {
              quality: d?.complaintCategoryData?.quality || 0,
              dispatch: d?.complaintCategoryData?.dispatch || 0,
              installation: d?.complaintCategoryData?.installation || 0,
              commercial: d?.complaintCategoryData?.commercial || 0,
            },
            srCategoryData: {
              labels: d?.srCategoryData?.labels || d?.srCategoryData?.Labels || [],
              srCounts: d?.srCategoryData?.srCounts || d?.srCategoryData?.SrCounts || [],
              categoryCodes:
                d?.srCategoryData?.categoryCodes || d?.srCategoryData?.CategoryCodes || [],
            },
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadZoneCharts = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("zone", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("zone", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            srsByZone: Array.isArray(d?.srsByZone ?? d?.SrsByZone)
              ? (d.srsByZone ?? d.SrsByZone)
              : [],
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadBvChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("bv", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("bv", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            productVerticalData: {
              verticals:
                d?.productVerticalData?.verticals ||
                d?.productVerticalData?.Verticals ||
                [],
              srCounts:
                d?.productVerticalData?.srCounts ||
                d?.productVerticalData?.SrCounts ||
                [],
              codes:
                d?.productVerticalData?.codes || d?.productVerticalData?.Codes || [],
            },
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadRepeatChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("repeat", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("repeat", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            repeatComplaintMonthLabels: Array.isArray(
              d?.repeatComplaintMonthLabels ?? d?.RepeatComplaintMonthLabels
            )
              ? (d.repeatComplaintMonthLabels ?? d.RepeatComplaintMonthLabels)
              : [],
            repeatComplaintCounts: Array.isArray(
              d?.repeatComplaintCounts ?? d?.RepeatComplaintCounts
            )
              ? (d.repeatComplaintCounts ?? d.RepeatComplaintCounts)
              : [],
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadSettlementChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("settlement", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("settlement", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            settlementTypeLabels: Array.isArray(
              d?.settlementTypeLabels ?? d?.SettlementTypeLabels
            )
              ? (d.settlementTypeLabels ?? d.SettlementTypeLabels)
              : [],
            settlementTypeCounts: Array.isArray(
              d?.settlementTypeCounts ?? d?.SettlementTypeCounts
            )
              ? (d.settlementTypeCounts ?? d.SettlementTypeCounts)
              : [],
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadMaxQueriesChart = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("maxQueries", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("maxQueries", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            maxQueries: Array.isArray(d?.maxQueries) ? d.maxQueries : [],
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadCountryTable = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("country", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("country", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            srsByCountry: Array.isArray(d?.srsByCountry ?? d?.SrsByCountry)
              ? (d.srsByCountry ?? d.SrsByCountry)
              : [],
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadPendingSettlementTable = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("pendingSettlement", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange(
          "pendingSettlement",
          overrideParams
        );
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            pendingSettlement: Array.isArray(d?.pendingSettlement) ? d.pendingSettlement : [],
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  const loadReopenedTable = React.useCallback(
    async (overrideParams = {}) => {
      await runChartLoader("reopened", async () => {
        const { start: fromDate, end: toDate } = getCardDateRange("reopened", overrideParams);
        const d = await fetchAdminDashboardPayload(overrideParams, {
          start: fromDate,
          end: toDate,
        });
        if (d) {
          setAdminDashboardData((prev) => ({
            ...prev,
            reopenedSrs: Array.isArray(d?.reopenedSrs) ? d.reopenedSrs : [],
          }));
        }
      });
    },
    [fetchAdminDashboardPayload, getCardDateRange, runChartLoader]
  );

  chartLoaderRefs.current = {
    openVsClosed: loadOpenVsClosedChart,
    sla: loadSlaChart,
    category: loadCategoryChart,
    zone: loadZoneCharts,
    bv: loadBvChart,
    repeat: loadRepeatChart,
    settlement: loadSettlementChart,
    maxQueries: loadMaxQueriesChart,
    country: loadCountryTable,
    pendingSettlement: loadPendingSettlementTable,
    reopened: loadReopenedTable,
  };

  const syncChartDateFiltersFromMaster = React.useCallback(
    (master = null) => {
      const source = master ?? {
        dateRange: filters.dateRange,
        dateRangeStart: filters.dateRangeStart,
        dateRangeEnd: filters.dateRangeEnd,
      };
      const cardDate = {
        dateRange: source.dateRange || "",
        dateRangeStart: source.dateRangeStart || "",
        dateRangeEnd: source.dateRangeEnd || "",
      };
      setChartCardDates(
        Object.fromEntries(ADMIN_CHART_DATE_KEYS.map((k) => [k, { ...cardDate }]))
      );
      ADMIN_CHART_DATE_KEYS.forEach((key) => {
        const ref = chartDatePickerRefs.current[key];
        if (ref?.current) $(ref.current).val(cardDate.dateRange);
      });
      const zoneAvgTatRef = chartDatePickerRefs.current.zoneAvgTat;
      if (zoneAvgTatRef?.current) $(zoneAvgTatRef.current).val(cardDate.dateRange);
      return cardDate;
    },
    [filters.dateRange, filters.dateRangeStart, filters.dateRangeEnd]
  );

  const renderChartLoaderOverlay = (widgetKey) =>
    chartLoading[widgetKey] ? (
      <div className="chart-widget-loading-overlay" aria-busy="true" aria-live="polite">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading</span>
        </div>
      </div>
    ) : null;

  const renderChartCardDateFilter = (cardKey, pickerKey = cardKey) => {
    const pickerRef = chartDatePickerRefs.current[pickerKey];
    const rangeLabel = (chartCardDates[cardKey]?.dateRange || "").trim();
    return (
      <div className="chart-card-date-filter">
        <input
          type="text"
          ref={(el) => {
            if (pickerRef) pickerRef.current = el;
          }}
          className="form-control form-control-light chart-card-date-input text-start"
          placeholder="Select date range"
          readOnly
          value={rangeLabel}
          style={{ width: `${getChartDateInputWidthPx(rangeLabel)}px` }}
        />
        <CalendarTodayIcon className="chart-card-date-icon" aria-hidden />
      </div>
    );
  };

  useEffect(() => {
    if (skipDateFilterDashboardLoadRef.current) {
      skipDateFilterDashboardLoadRef.current = false;
      return;
    }
    if (filters.dateRangeStart || filters.dateRangeEnd) {
      syncChartDateFiltersFromMaster();
      loadAllDashboardData({
        fromDate: filters.dateRangeStart,
        toDate: filters.dateRangeEnd,
        ...(filters.market && String(filters.market).trim()
          ? { market: filters.market }
          : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateRangeStart, filters.dateRangeEnd]);

  useEffect(() => {
    const applyZoneCardDate = ({ start, end, display }) => {
      setChartCardDates((prev) => ({
        ...prev,
        zone: {
          dateRange: display,
          dateRangeStart: start,
          dateRangeEnd: end,
        },
      }));
      chartLoaderRefs.current.zone?.({ fromDate: start, toDate: end });
      const zoneAvgTatRef = chartDatePickerRefs.current.zoneAvgTat;
      if (zoneAvgTatRef?.current) $(zoneAvgTatRef.current).val(display);
      const zoneRef = chartDatePickerRefs.current.zone;
      if (zoneRef?.current) $(zoneRef.current).val(display);
    };

    const cleanups = ADMIN_CHART_DATE_KEYS.map((cardKey) => {
      if (cardKey === "zone") {
        return initDashboardCardDateRangePicker(chartDatePickerRefs.current.zone?.current, {
          initialDisplay: chartCardDates.zone?.dateRange,
          onApply: applyZoneCardDate,
        });
      }
      return initDashboardCardDateRangePicker(chartDatePickerRefs.current[cardKey]?.current, {
        initialDisplay: chartCardDates[cardKey]?.dateRange,
        onApply: ({ start, end, display }) => {
          setChartCardDates((prev) => ({
            ...prev,
            [cardKey]: {
              dateRange: display,
              dateRangeStart: start,
              dateRangeEnd: end,
            },
          }));
          chartLoaderRefs.current[cardKey]?.({ fromDate: start, toDate: end });
        },
      });
    });

    cleanups.push(
      initDashboardCardDateRangePicker(chartDatePickerRefs.current.zoneAvgTat?.current, {
        initialDisplay: chartCardDates.zone?.dateRange,
        onApply: applyZoneCardDate,
      })
    );

    return () => cleanups.forEach((fn) => fn?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const shouldRestore =
      Boolean(location.state?.restoreAdminDashboard) ||
      Boolean(readAdminDashboardRestoreSnapshot());
    if (!shouldRestore) return;

    const saved = readAdminDashboardState();
    if (!saved) return;

    if (saved.filters) {
      setFilters({
        ...saved.filters,
        market:
          saved.filters.market && String(saved.filters.market).trim()
            ? saved.filters.market
            : DEFAULT_ADMIN_DASHBOARD_MARKET,
      });
    }
    if (saved.chartCollapsed) {
      setChartCollapsed(saved.chartCollapsed);
    }
    if (saved.filterExpanded != null) {
      setFilterExpanded(Boolean(saved.filterExpanded));
    }
    if (saved.startDate) setStartDate(saved.startDate);
    if (saved.endDate) setEndDate(saved.endDate);
    if (saved.dateRangeText) setDateRangeText(saved.dateRangeText);
    if (filterDateRangePickerRef.current && saved.filters?.dateRange) {
      $(filterDateRangePickerRef.current).val(saved.filters.dateRange);
    }
    if (saved.chartCardDates) {
      setChartCardDates(saved.chartCardDates);
      ADMIN_CHART_DATE_KEYS.forEach((key) => {
        const ref = chartDatePickerRefs.current[key];
        if (ref?.current && saved.chartCardDates[key]?.dateRange) {
          $(ref.current).val(saved.chartCardDates[key].dateRange);
        }
      });
    }
    markAdminDashboardRestored();
    const restored = saved.filters || {};
    const restoreParams = {
      fromDate: restored.dateRangeStart,
      toDate: restored.dateRangeEnd,
      status: restored.status,
      bv: restored.bv,
      country: restored.country,
      zone: restored.zone,
      region: restored.region,
      territory: restored.territory,
      invoiceNo: restored.invoiceNo,
      salesExecutiveName: restored.salesExecutiveName,
      complaintCategory: restored.complaintCategory,
      market:
        restored.market && String(restored.market).trim()
          ? restored.market
          : DEFAULT_ADMIN_DASHBOARD_MARKET,
    };
    loadAllDashboardData(restoreParams);
  }, [location.state?.restoreAdminDashboard, location.key, loadAllDashboardData]);

  // Handle filter button click
  const handleFilter = () => {
    const filterOverride = {
      complaintCategory: filters.complaintCategory,
      country: filters.country,
      zone: filters.zone,
      region: filters.region,
      territory: filters.territory,
      fromDate: filters.dateRangeStart,
      toDate: filters.dateRangeEnd,
      status: filters.status,
      bv: filters.bv,
      invoiceNo: filters.invoiceNo,
      salesExecutiveName: filters.salesExecutiveName,
      market: filters.market,
    };
    syncChartDateFiltersFromMaster();
    loadAllDashboardData(filterOverride);
  };

  // Handle reset button click
  const handleReset = () => {
    const fy = getDashboardFinancialYearRange();
    const defaultMarket = pickDefaultMarket(marketOptions);
    setFilters({
      dateRange: fy.dateRange,
      dateRangeStart: fy.dateRangeStart,
      dateRangeEnd: fy.dateRangeEnd,
      status: [],
      complaintCategory: "",
      bv: [],
      invoiceNo: "",
      salesExecutiveName: "",
      market: defaultMarket,
      country: [],
      zone: [],
      region: [],
      territory: [],
    });

    if (filterDateRangePickerRef.current) {
      $(filterDateRangePickerRef.current).val(fy.dateRange);
    }

    if (statusSelectRef.current) {
      $(statusSelectRef.current).val(null).trigger("change");
    }

    if (bvSelectRef.current) {
      $(bvSelectRef.current).val(null).trigger("change");
    }

    setShowInvoiceSuggestions(false);
    setInvoiceSuggestions([]);
    syncChartDateFiltersFromMaster({
      dateRange: fy.dateRange,
      dateRangeStart: fy.dateRangeStart,
      dateRangeEnd: fy.dateRangeEnd,
    });

    const resetOverride = {
      fromDate: fy.dateRangeStart,
      toDate: fy.dateRangeEnd,
      status: [],
      bv: [],
      country: [],
      zone: [],
      region: [],
      territory: [],
      invoiceNo: "",
      salesExecutiveName: "",
      complaintCategory: "",
      market: defaultMarket,
    };
    loadAllDashboardData(resetOverride);
  };

  // Load zone names for charts
  const loadZoneNames = async () => {
    try {
      const response = await zoneMasterAPI.getPaged({
        status: "active",
        pageNumber: 1,
        pageSize: 100, // Get all active zones
      });
      if (response?.success && response.data?.zones) {
        const zones = response.data.zones; // Note: lowercase 'zones' not 'Zones'
        // Extract zone names - property is zoneName (camelCase)
        const names = zones.map((zone) => zone.zoneName || "").filter(name => name);
        setZoneNames(names);
        setZonesData(zones); // Store zones with codes for click handler
        return zones; // Return zones with codes/names for data loading
      }
    } catch (e) {
      console.error("Error loading zone names:", e);
      // Keep default zone names on error
    }
    return [];
  };

  // Load SR counts and TAT by zone
  const loadSRDataByZone = async () => {
    try {
      const zones = await loadZoneNames();
      
      // Get zone names in the same order as zones array (this matches what was set in zoneNames state)
      const zoneNamesOrdered = zones.map(z => z.zoneName).filter(n => n);

      // Create a map of zone codes to zone names
      const zoneMap = {};
      zones.forEach((zone) => {
        const zoneCode = zone.zoneCode;
        const zoneName = zone.zoneName;
        if (zoneCode && zoneName) {
          zoneMap[zoneCode] = zoneName;
        }
      });

      // Fetch all complaints (with large page size to get all data)
      // Note: In production, this should be replaced with a dedicated dashboard statistics API
      const complaintsResponse = await complaintAPI.getAll({
        pageNumber: 1,
        pageSize: 10000, // Large page size to get all complaints
      });

      if (complaintsResponse?.success && complaintsResponse.data?.Complaints) {
        const complaints = complaintsResponse.data.Complaints;
        
        // Initialize zone data
        const zoneCounts = {};
        const zoneTATs = {};
        
        zones.forEach((zone) => {
          const zoneCode = zone.zoneCode;
          if (zoneCode) {
            zoneCounts[zoneCode] = 0;
            zoneTATs[zoneCode] = [];
          }
        });

        // Aggregate complaints by zone
        complaints.forEach((complaint) => {
          // Get zone code from complaint (adjust property names based on actual API response)
          const zoneCode = complaint.zoneCode || complaint.ZoneCode || 
                          complaint.territory?.zoneCode || complaint.Territory?.ZoneCode;
          
          if (zoneCode && zoneCounts.hasOwnProperty(zoneCode)) {
            zoneCounts[zoneCode]++;
            
            // Calculate TAT if dates are available
            if (complaint.createdDate && complaint.closedDate) {
              const created = new Date(complaint.createdDate);
              const closed = new Date(complaint.closedDate);
              const tatDays = (closed - created) / (1000 * 60 * 60 * 24);
              if (!isNaN(tatDays) && tatDays >= 0) {
                zoneTATs[zoneCode].push(tatDays);
              }
            }
          }
        });

        // Prepare data arrays in the same order as zoneNames
        // Create a map of zoneName to zoneCode for lookup
        const zoneNameToCodeMap = {};
        zones.forEach((zone) => {
          if (zone.zoneName && zone.zoneCode) {
            zoneNameToCodeMap[zone.zoneName] = zone.zoneCode;
          }
        });

        // Use zoneNamesOrdered to ensure exact order match with zoneNames state
        const counts = zoneNamesOrdered.map((zoneName) => {
          const zoneCode = zoneNameToCodeMap[zoneName];
          return zoneCode ? (zoneCounts[zoneCode] || 0) : 0;
        });
        
        const tats = zoneNamesOrdered.map((zoneName) => {
          const zoneCode = zoneNameToCodeMap[zoneName];
          const tatArray = zoneCode ? (zoneTATs[zoneCode] || []) : [];
          const avgTAT = tatArray.length > 0
            ? tatArray.reduce((sum, tat) => sum + tat, 0) / tatArray.length
            : 0;
          return Math.round(avgTAT);
        });

        setSrsByZoneCounts(counts);
        setAvgTATByZone(tats);
      }
    } catch (e) {
      console.error("Error loading SR data by zone:", e);
      // Keep default data on error
    }
  };

  // Load initial data (wait for default market before first dashboard fetch)
  useEffect(() => {
    let cancelled = false;
    loadStatusOptions();
    loadMarketOptions().then((market) => {
      if (cancelled) return;
      if (market) {
        loadCountryOptions();
        if (isExportMarketSelected(market, marketOptions)) {
          loadZoneOptions();
        }
      }
      loadZoneNames();
      const fy = getDashboardFinancialYearRange();
      const domesticMarket = market || DEFAULT_ADMIN_DASHBOARD_MARKET;
      const initialParams = {
        fromDate: filters.dateRangeStart || fy.dateRangeStart,
        toDate: filters.dateRangeEnd || fy.dateRangeEnd,
        market: domesticMarket,
      };
      loadAllDashboardData(initialParams);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Business Vertical + SR Category scoped to selected Market
  useEffect(() => {
    if (!filters.market) {
      setBvOptions([]);
      setSrCategoryOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      await Promise.all([
        loadBVOptions(filters.market),
        loadSrCategoryOptions(filters.market),
      ]);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.market]);

  // Export: load all zones when market is Export
  useEffect(() => {
    if (!isExportCascade) return;
    loadZoneOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade]);

  // Domestic: load zones when country changes (skip when Country = All)
  useEffect(() => {
    if (isExportCascade) return;
    if (isGeoAllFilterValue(filters.country)) {
      setZoneOptions([GEO_ALL_OPTION]);
      setRegionOptions([GEO_ALL_OPTION]);
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    if (filters.country?.length) {
      setZoneOptions([]);
      loadZoneOptions(filters.country);
    } else {
      setZoneOptions([]);
      setFilters((prev) => ({ ...prev, zone: [], region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade, filters.country]);

  // Clear geo when switching Export ↔ Domestic
  useEffect(() => {
    if (prevExportCascadeRef.current === null) {
      prevExportCascadeRef.current = isExportCascade;
      return;
    }
    if (prevExportCascadeRef.current === isExportCascade) return;
    prevExportCascadeRef.current = isExportCascade;

    setZoneOptions([]);
    setRegionOptions([]);
    setTerritoryOptions([]);
    setFilters((prev) => ({
      ...prev,
      country: [],
      zone: [],
      region: [],
      territory: [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExportCascade]);

  // Refresh zone labels when country names load (Domestic: Zone (Country); Export: zone name only)
  useEffect(() => {
    if (!zoneOptions.length || !countryOptions.length) return;
    if (isGeoAllFilterValue(filters.zone)) return;
    setZoneOptions((prev) =>
      prev.map((z) => {
        if (isGeoAllSelectionToken(z.id) || isGeoAllOptionRow(z.id, z.text)) {
          return z;
        }
        return {
          ...z,
          text: formatZoneOptionLabel(z, countryOptions, {
            includeCountry: !isExportCascade,
          }),
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOptions, isExportCascade]);

  // Regions: require zone (+ country for Export); skip when parent is All
  useEffect(() => {
    if (isGeoAllFilterValue(filters.zone) || isGeoAllFilterValue(filters.country)) {
      setRegionOptions([GEO_ALL_OPTION]);
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    const zOk = filters.zone?.length;
    const cOk = !isExportCascade || filters.country?.length;
    if (zOk && cOk) {
      setRegionOptions([]);
      regionOptionsRef.current = [];
      loadRegionOptions(filters.zone, filters.country, zoneOptions, isExportCascade);
    } else {
      setRegionOptions([]);
      regionOptionsRef.current = [];
      setFilters((prev) => ({ ...prev, region: [], territory: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.zone, filters.country, isExportCascade, zoneOptions]);

  // Territories for selected region(s); skip when parent is All
  useEffect(() => {
    if (
      isGeoAllFilterValue(filters.region) ||
      isGeoAllFilterValue(filters.zone) ||
      isGeoAllFilterValue(filters.country)
    ) {
      setTerritoryOptions([GEO_ALL_OPTION]);
      return;
    }
    if (!filters.region?.length) {
      setTerritoryOptions([]);
      setFilters((prev) => ({ ...prev, territory: [] }));
      return;
    }
    const regionsReady =
      regionOptions.length > 0 || regionOptionsRef.current.length > 0;
    if (!regionsReady) return;

    setTerritoryOptions([]);
    loadTerritoryOptions(
      filters.region,
      zoneOptions.length ? zoneOptions : zoneOptionsRef.current,
      regionOptions.length ? regionOptions : regionOptionsRef.current,
      filters.zone,
      filters.country
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.zone, filters.country, regionOptions, zoneOptions]);

  // Initialize filter date range picker (similar to ApproverDashboard)
  useEffect(() => {
    // Only initialize if filter panel is expanded and input exists
    if (!filterExpanded || !filterDateRangePickerRef.current) return;

    const $input = $(filterDateRangePickerRef.current);
    if (!$input.length) return;

    const initDateRangePicker = () => {
      // Destroy existing instance if any
      if ($input.data("daterangepicker")) {
        $input.data("daterangepicker").remove();
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
      }

      try {
        $input.daterangepicker({
          autoUpdateInput: false,
          locale: {
            cancelLabel: "Clear",
            applyLabel: "Apply",
            format: "DD-MM-YYYY",
          },
          opens: "left",
          drops: "down",
          showDropdowns: true,
          ranges: getDashboardDateRangePresets(),
        });

        $input.on("apply.daterangepicker", function (ev, picker) {
          const displayText =
            picker.startDate.format("DD-MM-YYYY") +
            " - " +
            picker.endDate.format("DD-MM-YYYY");

          setFilters((prev) => ({
            ...prev,
            dateRange: displayText,
            dateRangeStart: picker.startDate.format("YYYY-MM-DD"),
            dateRangeEnd: picker.endDate.format("YYYY-MM-DD"),
          }));
          $(this).val(displayText);
        });

        $input.on("cancel.daterangepicker", function (ev, picker) {
          setFilters((prev) => ({ ...prev, dateRange: "" }));
          $(this).val("");
        });

        if (filters.dateRange) {
          $input.val(filters.dateRange);
        }
      } catch (error) {
        console.error("Error initializing daterangepicker:", error);
      }
    };

    // Initialize with a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initDateRangePicker();
    }, 100);

    // Reinitialize on click/focus if not working
    const handleInteraction = () => {
      if (!$input.data("daterangepicker")) {
        initDateRangePicker();
      }
    };

    $input.on("click.daterangepicker-reinit", handleInteraction);
    $input.on("focus.daterangepicker-reinit", handleInteraction);

    // Cleanup
    return () => {
      clearTimeout(timer);
      if ($input.data("daterangepicker")) {
        $input.off("apply.daterangepicker");
        $input.off("cancel.daterangepicker");
        $input.off("click.daterangepicker-reinit");
        $input.off("focus.daterangepicker-reinit");
        $input.data("daterangepicker").remove();
      }
    };
  }, [filterExpanded]);

  // Status is now handled by CustomMultiSelect; no Select2 initialization needed
  useEffect(() => {}, [filterExpanded]);

  // SR Category uses a controlled native <select> (no Select2) to avoid layout jump on Market change.

  // BV is handled by CustomMultiSelect; no Select2 initialization required here
  useEffect(() => {}, [filterExpanded, bvOptions]);

  const pieLabelsToExportRows = (labels, data) => {
    const d = Array.isArray(data) ? data.map((x) => Number(x) || 0) : [];
    const L = Array.isArray(labels) ? labels : [];
    const total = d.reduce((a, b) => a + b, 0);
    const rows = L.map((label, i) => {
      const v = d[i] ?? 0;
      const pct = total ? `${((v / total) * 100).toFixed(2)}%` : "0%";
      return { Category: label, Count: v, Percentage: pct };
    });
    rows.push({
      Category: "Total",
      Count: total,
      Percentage: total ? "100%" : "0%",
    });
    return rows;
  };

  const barLabelsToExportRows = (labels, data, labelKey, valueKey) => {
    const L = Array.isArray(labels) ? labels : [];
    const D = Array.isArray(data) ? data.map((x) => Number(x) || 0) : [];
    const rows = L.map((label, i) => ({
      [labelKey]: label,
      [valueKey]: D[i] ?? 0,
    }));
    const total = D.reduce((a, b) => a + b, 0);
    rows.push({ [labelKey]: "Total", [valueKey]: total });
    return rows;
  };

  const getAvgTatByZoneExportRows = () =>
    avgTATByZoneData.labels.map((lab, i) => ({
      Zone: lab,
      "Avg TAT (Days)": avgTATByZoneData.data[i] ?? 0,
    }));

  const getSlaComplianceExportRows = () => {
    const v = Math.min(
      100,
      Math.max(0, Number(adminDashboardData.slaCompliancePercent) || 0)
    );
    return [{ Metric: "SLA Compliance %", Value: `${v}%` }];
  };

  const getSrsByCountryExportRows = () =>
    srsByCountryData.map((r) => ({
      Country: r.country,
      Count: r.count,
      "%": `${r.percentage}%`,
    }));

  const getPendingSettlementExportRows = () =>
    pendingSettlementData.map((r) => ({
      "SR No": r.srNo,
      Amount: r.amount,
      "Days Pending": r.daysPending,
    }));

  const getReopenedSrsExportRows = () =>
    reopenedSRsData.map((r) => ({
      "SR No": r.srNo,
      Date: r.reopenedDate,
      Reason: r.reason,
    }));

  const handleAdminOpenVsClosedExcel = async () => {
    const rows = pieLabelsToExportRows(
      openVsClosedChartData.labels,
      openVsClosedChartData.datasets[0].data
    );
    await exportJsonToExcel(
      rows,
      `admin-open-vs-closed-${formatDateDDMMYYYY()}.xlsx`,
      "Open vs Closed"
    );
  };
  const handleAdminOpenVsClosedCsv = () => {
    exportRowsToCsv(
      pieLabelsToExportRows(
        openVsClosedChartData.labels,
        openVsClosedChartData.datasets[0].data
      ),
      `admin-open-vs-closed-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleAdminOpenVsClosedPdf = () => {
    exportRowsToPdf(
      pieLabelsToExportRows(
        openVsClosedChartData.labels,
        openVsClosedChartData.datasets[0].data
      ),
      `admin-open-vs-closed-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "portrait", fontSize: 9 }
    );
  };

  const handleAdminSlaExcel = async () => {
    await exportJsonToExcel(
      getSlaComplianceExportRows(),
      `admin-sla-compliance-${formatDateDDMMYYYY()}.xlsx`,
      "SLA Compliance"
    );
  };
  const handleAdminSlaCsv = () => {
    exportRowsToCsv(
      getSlaComplianceExportRows(),
      `admin-sla-compliance-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleAdminSlaPdf = () => {
    exportRowsToPdf(
      getSlaComplianceExportRows(),
      `admin-sla-compliance-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "portrait", fontSize: 10 }
    );
  };

  const handleAdminCategoryExcel = async () => {
    const rows = pieLabelsToExportRows(srsByCategoryData.labels, srsByCategoryData.data);
    await exportJsonToExcel(
      rows,
      `admin-srs-by-category-${formatDateDDMMYYYY()}.xlsx`,
      "SRs by Category"
    );
  };
  const handleAdminCategoryCsv = () => {
    exportRowsToCsv(
      pieLabelsToExportRows(srsByCategoryData.labels, srsByCategoryData.data),
      `admin-srs-by-category-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleAdminCategoryPdf = () => {
    exportRowsToPdf(
      pieLabelsToExportRows(srsByCategoryData.labels, srsByCategoryData.data),
      `admin-srs-by-category-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const handleAdminZoneExcel = async () => {
    const rows = barLabelsToExportRows(
      srsByZoneData.labels,
      srsByZoneData.data,
      "Zone",
      "SR Count"
    );
    await exportJsonToExcel(
      rows,
      `admin-srs-by-zone-${formatDateDDMMYYYY()}.xlsx`,
      "SRs by Zone"
    );
  };
  const handleAdminZoneCsv = () => {
    exportRowsToCsv(
      barLabelsToExportRows(
        srsByZoneData.labels,
        srsByZoneData.data,
        "Zone",
        "SR Count"
      ),
      `admin-srs-by-zone-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleAdminZonePdf = () => {
    exportRowsToPdf(
      barLabelsToExportRows(
        srsByZoneData.labels,
        srsByZoneData.data,
        "Zone",
        "SR Count"
      ),
      `admin-srs-by-zone-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const handleAdminAvgTatZoneExcel = async () => {
    const rows = getAvgTatByZoneExportRows();
    if (!rows.length) return;
    await exportJsonToExcel(
      rows,
      `admin-avg-tat-by-zone-${formatDateDDMMYYYY()}.xlsx`,
      "Avg TAT by Zone"
    );
  };
  const handleAdminAvgTatZoneCsv = () => {
    const rows = getAvgTatByZoneExportRows();
    if (!rows.length) return;
    exportRowsToCsv(rows, `admin-avg-tat-by-zone-${formatDateDDMMYYYY()}.csv`);
  };
  const handleAdminAvgTatZonePdf = () => {
    const rows = getAvgTatByZoneExportRows();
    if (!rows.length) return;
    exportRowsToPdf(
      rows,
      `admin-avg-tat-by-zone-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const handleAdminProductExcel = async () => {
    const rows = barLabelsToExportRows(
      srsByProductData.labels,
      srsByProductData.data,
      "Business Vertical",
      "SR Count"
    );
    await exportJsonToExcel(
      rows,
      `admin-srs-by-business-vertical-${formatDateDDMMYYYY()}.xlsx`,
      "SRs by Business Vertical"
    );
  };
  const handleAdminProductCsv = () => {
    exportRowsToCsv(
      barLabelsToExportRows(
        srsByProductData.labels,
        srsByProductData.data,
        "Business Vertical",
        "SR Count"
      ),
      `admin-srs-by-business-vertical-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleAdminProductPdf = () => {
    exportRowsToPdf(
      barLabelsToExportRows(
        srsByProductData.labels,
        srsByProductData.data,
        "Business Vertical",
        "SR Count"
      ),
      `admin-srs-by-business-vertical-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const handleAdminRepeatExcel = async () => {
    const rows = barLabelsToExportRows(
      repeatComplaintsData.labels,
      repeatComplaintsData.data,
      "Month",
      "Repeat Count"
    );
    await exportJsonToExcel(
      rows,
      `admin-repeat-complaints-${formatDateDDMMYYYY()}.xlsx`,
      "Repeat Complaints"
    );
  };
  const handleAdminRepeatCsv = () => {
    exportRowsToCsv(
      barLabelsToExportRows(
        repeatComplaintsData.labels,
        repeatComplaintsData.data,
        "Month",
        "Repeat Count"
      ),
      `admin-repeat-complaints-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleAdminRepeatPdf = () => {
    exportRowsToPdf(
      barLabelsToExportRows(
        repeatComplaintsData.labels,
        repeatComplaintsData.data,
        "Month",
        "Repeat Count"
      ),
      `admin-repeat-complaints-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const handleAdminSettlementExcel = async () => {
    const rows = pieLabelsToExportRows(
      settlementTypeData.labels,
      settlementTypeData.data
    );
    await exportJsonToExcel(
      rows,
      `admin-settlement-type-${formatDateDDMMYYYY()}.xlsx`,
      "Settlement Type"
    );
  };
  const handleAdminSettlementCsv = () => {
    exportRowsToCsv(
      pieLabelsToExportRows(settlementTypeData.labels, settlementTypeData.data),
      `admin-settlement-type-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleAdminSettlementPdf = () => {
    exportRowsToPdf(
      pieLabelsToExportRows(settlementTypeData.labels, settlementTypeData.data),
      `admin-settlement-type-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const handleAdminMaxQueriesExcel = async () => {
    const rows = barLabelsToExportRows(
      maxQueriesData.labels,
      maxQueriesData.data,
      "SR No",
      "Query Count"
    );
    await exportJsonToExcel(
      rows,
      `admin-max-queries-${formatDateDDMMYYYY()}.xlsx`,
      "SRs with Max Queries"
    );
  };
  const handleAdminMaxQueriesCsv = () => {
    exportRowsToCsv(
      barLabelsToExportRows(
        maxQueriesData.labels,
        maxQueriesData.data,
        "SR No",
        "Query Count"
      ),
      `admin-max-queries-${formatDateDDMMYYYY()}.csv`
    );
  };
  const handleAdminMaxQueriesPdf = () => {
    exportRowsToPdf(
      barLabelsToExportRows(
        maxQueriesData.labels,
        maxQueriesData.data,
        "SR No",
        "Query Count"
      ),
      `admin-max-queries-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const handleAdminCountryExcel = async () => {
    const rows = getSrsByCountryExportRows();
    if (!rows.length) return;
    await exportJsonToExcel(
      rows,
      `admin-srs-by-country-${formatDateDDMMYYYY()}.xlsx`,
      "SRs by Country"
    );
  };
  const handleAdminCountryCsv = () => {
    const rows = getSrsByCountryExportRows();
    if (!rows.length) return;
    exportRowsToCsv(rows, `admin-srs-by-country-${formatDateDDMMYYYY()}.csv`);
  };
  const handleAdminCountryPdf = () => {
    const rows = getSrsByCountryExportRows();
    if (!rows.length) return;
    exportRowsToPdf(
      rows,
      `admin-srs-by-country-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "portrait", fontSize: 9 }
    );
  };

  const handleAdminPendingExcel = async () => {
    const rows = getPendingSettlementExportRows();
    if (!rows.length) return;
    await exportJsonToExcel(
      rows,
      `admin-pending-settlement-${formatDateDDMMYYYY()}.xlsx`,
      "Pending Settlement"
    );
  };
  const handleAdminPendingCsv = () => {
    const rows = getPendingSettlementExportRows();
    if (!rows.length) return;
    exportRowsToCsv(rows, `admin-pending-settlement-${formatDateDDMMYYYY()}.csv`);
  };
  const handleAdminPendingPdf = () => {
    const rows = getPendingSettlementExportRows();
    if (!rows.length) return;
    exportRowsToPdf(
      rows,
      `admin-pending-settlement-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  const handleAdminReopenedExcel = async () => {
    const rows = getReopenedSrsExportRows();
    if (!rows.length) return;
    await exportJsonToExcel(
      rows,
      `admin-reopened-srs-${formatDateDDMMYYYY()}.xlsx`,
      "Reopened SRs"
    );
  };
  const handleAdminReopenedCsv = () => {
    const rows = getReopenedSrsExportRows();
    if (!rows.length) return;
    exportRowsToCsv(rows, `admin-reopened-srs-${formatDateDDMMYYYY()}.csv`);
  };
  const handleAdminReopenedPdf = () => {
    const rows = getReopenedSrsExportRows();
    if (!rows.length) return;
    exportRowsToPdf(
      rows,
      `admin-reopened-srs-${formatDateDDMMYYYY()}.pdf`,
      { orientation: "landscape", fontSize: 7 }
    );
  };

  return (
    <div className="admin-dashboard-container">
      <div className="container-fluid">
        {/* Filter Card - Same as ApproverDashboard */}
        <div className="row mb-3">
          <div className="col-12">
            <div
              className="card-panel mb-3 filter-panel"
              style={{ paddingBottom: filterExpanded ? "15px" : "2px" }}
            >
              <div
                className={`section-heading d-flex align-items-center gap-2 ${
                  filterExpanded ? "mb-3" : "filter-panel-heading-collapsed"
                }`}
                style={{
                  marginBottom: filterExpanded ? "14px" : 0,
                  cursor: "pointer",
                }}
                onClick={() => setFilterExpanded(!filterExpanded)}
              >
                <FilterListIcon
                  fontSize="small"
                  className="text-muted"
                  style={{
                    width: "18px",
                    height: "18px",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                />
                <span>Filter</span>
                <span className="filter-panel-active-daterange ms-auto">
                  {filters.dateRange || defaultFY.dateRange || "Select date range"}
                </span>
                {filterExpanded ? (
                  <ExpandLessIcon
                    fontSize="small"
                    className="text-muted"
                    style={{
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    className="text-muted"
                    style={{
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>

              {filterExpanded && (
                <div className="container-fluid">
                  <div className="row g-3 align-items-end">
                    {/* Date Range */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Select Date Range
                      </label>
                      <div className="input-icon" style={{ position: "relative" }}>
                        <input
                          type="text"
                          ref={filterDateRangePickerRef}
                          className="form-control form-control-light text-start"
                          placeholder="Select date range"
                          readOnly
                          style={{ height: "42px", textAlign: "left" }}
                        />
                        <CalendarTodayIcon
                          className="icon-right"
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            width: "16px",
                            height: "16px",
                            color: "#6c757d",
                          }}
                        />
                      </div>
                    </div>

                    {/* Status Dropdown */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Status
                      </label>
                      <select
                        ref={statusSelectRef}
                        className="form-select form-control-light filter-select text-start status-multiselect"
                        style={{ height: "42px", textAlign: "left", display: "none" }}
                        multiple
                      >
                        {statusOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.text}
                          </option>
                        ))}
                      </select>
                      <CustomMultiSelect
                        options={statusOptions}
                        value={filters.status || []}
                        onChange={(selected) =>
                          setFilters((prev) => ({
                            ...prev,
                            status: Array.isArray(selected) ? selected : [],
                          }))
                        }
                        placeholder="Select Status"
                        disabled={false}
                      />
                    </div>

                    {/* Market Dropdown — after Status; drives SR Category + BV */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Market
                      </label>
                      <select
                        className="form-select form-control-light filter-select text-start"
                        value={filters.market || ""}
                        disabled={!marketOptions.length}
                        onChange={(e) => {
                          const nextMarket = e.target.value || "";
                          setFilters((prev) => ({
                            ...prev,
                            market: nextMarket,
                            complaintCategory: "",
                            country: [],
                            zone: [],
                            region: [],
                            territory: [],
                            bv: [],
                          }));
                        }}
                        style={{ height: "42px", textAlign: "left" }}
                      >
                        <option value="">Select Market</option>
                        {marketOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* SR Category — from Greenlam.SRTYPES by selected Market */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        SR Category
                      </label>
                      <select
                        key={`sr-cat-${filters.market || "none"}`}
                        className="form-select form-control-light filter-select text-start"
                        style={{ height: "42px", textAlign: "left" }}
                        disabled={!filters.market}
                        value={filters.complaintCategory || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            complaintCategory: e.target.value || "",
                          }))
                        }
                      >
                        <option value="">
                          {!filters.market
                            ? "Select Market first"
                            : "Select SR Category"}
                        </option>
                        {srCategoryOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Business Vertical filter */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Business Vertical
                      </label>
                      <select
                        ref={bvSelectRef}
                        className="form-select form-control-light filter-select text-start"
                        style={{ height: "42px", textAlign: "left", display: "none" }}
                        multiple
                      />
                      <CustomMultiSelect
                        options={bvOptions}
                        value={filters.bv || []}
                        onChange={(selected) =>
                          setFilters((prev) => ({
                            ...prev,
                            bv: Array.isArray(selected) ? selected : [],
                          }))
                        }
                        placeholder="Select Business Vertical"
                        disabled={false}
                      />
                    </div>

                    {/* Invoice No. Input — hidden per product request */}
                    <div className="col-12 col-md-6 col-lg-3 d-none">
                      <label className="input-label d-block mb-1 text-start">
                        SR Number / Invoice Number
                      </label>
                      <div className="input-icon" style={{ position: "relative" }}>
                        <input
                          type="text"
                          className="form-control form-control-light text-start"
                          placeholder="Enter invoice number"
                          value={filters.invoiceNo}
                          onChange={handleInvoiceInputChange}
                          style={{ height: "42px", textAlign: "left" }}
                        />
                        <SearchIcon
                          className="icon-right"
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            width: "16px",
                            height: "16px",
                            color: "#6c757d",
                          }}
                        />
                        {showInvoiceSuggestions &&
                          invoiceSuggestions.length > 0 && (
                            <ul
                              className="invoice-suggestions-list"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                backgroundColor: "white",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                marginTop: "4px",
                                maxHeight: "200px",
                                overflowY: "auto",
                                zIndex: 1000,
                                listStyle: "none",
                                padding: 0,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                            >
                              {invoiceSuggestions.map((suggestion, index) => (
                                <li
                                  key={index}
                                  onClick={() =>
                                    handleInvoiceSuggestionClick(suggestion)
                                  }
                                  style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #eee",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = "#f5f5f5";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = "white";
                                  }}
                                >
                                  {suggestion}
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                    </div>

                    {/* Sales Executive Name Input — hidden per product request */}
                    <div className="col-12 col-md-6 col-lg-3 d-none">
                      <label className="input-label d-block mb-1 text-start">
                        Sales Executive name
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-light text-start"
                        placeholder="Enter sales executive name"
                        value={filters.salesExecutiveName}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            salesExecutiveName: e.target.value,
                          }))
                        }
                        style={{ height: "42px", textAlign: "left" }}
                      />
                    </div>

                    {(isExportCascade ? ["zone", "country", "region", "territory"] : ["country", "zone", "region", "territory"]).map((geoKey) => {
                      const countryIsAll = isGeoAllFilterValue(filters.country);
                      const zoneIsAll = isGeoAllFilterValue(filters.zone);
                      const regionIsAll = isGeoAllFilterValue(filters.region);
                      if (geoKey === "country") {
                        const countryList = isExportCascade
                          ? exportCountryOptions
                          : countryOptions;
                        return (
                          <div key="country" className="col-12 col-md-6 col-lg-3">
                            <label className="input-label d-block mb-1 text-start">
                              Country
                            </label>
                            <CustomMultiSelect
                              options={countryList}
                              value={filters.country || []}
                              onChange={(selected) =>
                                handleGeoFilterChange("country", selected, countryList)
                              }
                              placeholder={
                                isExportCascade
                                  ? filters.zone?.length
                                    ? "Select Country"
                                    : "Select zone first"
                                  : "Select Country"
                              }
                              disabled={
                                !filters.market ||
                                (isExportCascade &&
                                  (!filters.zone?.length || zoneIsAll))
                              }
                              loading={geoLoading.country}
                              loadingText="Loading countries..."
                              showSelectAll={!countryIsAll && !zoneIsAll}
                            />
                          </div>
                        );
                      }
                      if (geoKey === "zone") {
                        return (
                          <div key="zone" className="col-12 col-md-6 col-lg-3">
                            <label className="input-label d-block mb-1 text-start">
                              Zone
                            </label>
                            <CustomMultiSelect
                              options={zoneOptions}
                              value={filters.zone || []}
                              onChange={(selected) =>
                                handleGeoFilterChange("zone", selected, zoneOptions)
                              }
                              placeholder={
                                !filters.market
                                  ? "Select market first"
                                  : isExportCascade
                                    ? "Select zone(s)"
                                    : filters.country?.length
                                      ? "Select zone(s)"
                                      : "Select country first"
                              }
                              disabled={
                                !filters.market ||
                                (!isExportCascade &&
                                  (!filters.country?.length || countryIsAll))
                              }
                              loading={geoLoading.zone}
                              loadingText="Loading zones..."
                              showSelectAll={!zoneIsAll && !countryIsAll}
                            />
                          </div>
                        );
                      }
                      if (geoKey === "region") {
                        const regionReady = isExportCascade
                          ? filters.zone?.length && filters.country?.length
                          : filters.zone?.length;
                        const lockedByAll =
                          countryIsAll || zoneIsAll || regionIsAll;
                        return (
                          <div key="region" className="col-12 col-md-6 col-lg-3">
                            <label className="input-label d-block mb-1 text-start">
                              Region
                            </label>
                            <CustomMultiSelect
                              options={regionOptions}
                              value={filters.region || []}
                              onChange={(selected) =>
                                handleGeoFilterChange("region", selected, regionOptions)
                              }
                              placeholder={
                                regionReady
                                  ? "Select region(s)"
                                  : isExportCascade
                                    ? "Select country first"
                                    : "Select zone first"
                              }
                              disabled={
                                !filters.market || !regionReady || lockedByAll
                              }
                              loading={geoLoading.region}
                              loadingText="Loading regions..."
                              showSelectAll={!lockedByAll}
                            />
                          </div>
                        );
                      }
                      const territoryLockedByAll =
                        countryIsAll || zoneIsAll || regionIsAll;
                      return (
                        <div key="territory" className="col-12 col-md-6 col-lg-3">
                          <label className="input-label d-block mb-1 text-start">
                            Territory
                          </label>
                          <CustomMultiSelect
                            options={territoryOptions}
                            value={filters.territory || []}
                            onChange={(selected) =>
                              handleGeoFilterChange(
                                "territory",
                                selected,
                                territoryOptions
                              )
                            }
                            placeholder={
                              filters.region?.length
                                ? "Select territory(ies)"
                                : "Select region first"
                            }
                            disabled={
                              !filters.market ||
                              !filters.region?.length ||
                              territoryLockedByAll
                            }
                            loading={geoLoading.territory}
                            loadingText="Loading territories..."
                            showSelectAll={!territoryLockedByAll}
                            selectAllLabel={
                              territoryOptions.length > 0
                                ? `Select all territories (${territoryOptions.length})`
                                : "Select all territories"
                            }
                          />
                        </div>
                      );
                    })}

                    {/* Action Buttons */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label
                        className="input-label d-block mb-1"
                        style={{ visibility: "hidden" }}
                      >
                        Actions
                      </label>
                      <div className="d-flex gap-2 filter-buttons">
                        <button
                          type="button"
                          className="btn btn-filter"
                          onClick={handleFilter}
                          style={{
                            background:
                              "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                            border: "none",
                            color: "white",
                            fontWeight: 600,
                            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                            borderRadius: "4px",
                            padding: "8px 24px",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <FilterListIcon fontSize="small" className="me-1" />
                          Filter
                        </button>
                        <button
                          type="button"
                          className="btn btn-reset-filter"
                          onClick={handleReset}
                          title="Reset"
                          style={{
                            background:
                              "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                            border: "none",
                            color: "white",
                            fontWeight: 600,
                            boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                            borderRadius: "4px",
                            padding: "8px 16px",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <RestartAltIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KPI Tiles Row */}
        <div className="row admin-kpi-loader-wrap">
          {renderChartLoaderOverlay(ADMIN_KPI_WIDGET_KEY)}
          <div className="col-12 col-md-6 col-lg-3 mb-3">
            <div className="sr-count-card pending" onClick={() => navigateAdminDrilldown({})}>
              <div className="sr-count-label">Total SRs</div>
              <div className="sr-count-subtitle">Overall count</div>
              <div className="sr-count-value">{totalSRs}</div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3 mb-3">
            <div
              className="sr-count-card open"
              onClick={() => navigateAdminDrilldown({ statusToken: 5 })}
              title="View final closed SRs (Avg TAT)"
            >
              <div className="sr-count-label">Avg TAT (Overall days)</div>
              <div className="sr-count-subtitle">Average turnaround time (days)</div>
              <div className="sr-count-value">
                {(() => {
                  const closedCnt = Number(adminDashboardData?.srData?.closedSRCount) || 0;
                  const tat =
                    adminDashboardData?.srData?.avgApprovalTatDays ??
                    adminDashboardData?.overallAvgTatDays;
                  if (
                    closedCnt === 0 ||
                    tat === null ||
                    tat === undefined ||
                    tat === ""
                  ) {
                    return "–";
                  }
                  return String(Math.round(Number(tat)));
                })()}
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3 mb-3">
            <div
              className="sr-count-card special approved-amount-card"
              onClick={() => navigateAdminDrilldown({ statusToken: 5 })}
              title="View final closed SRs (amount)"
            >
              <div className="sr-count-label">Total Final Closed Amount</div>
              <div className="sr-count-subtitle">Final closed amount per SR</div>
              <div className="sr-count-value approved-amount-small">
                {(() => {
                  const inr = Number(adminDashboardData.totalApprovedAmountInr) || 0;
                  const usd = Number(adminDashboardData.totalApprovedAmountUsd) || 0;
                  const parts = [];
                  if (inr > 0) {
                    parts.push(
                      `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(inr)}`
                    );
                  }
                  if (usd > 0) {
                    parts.push(
                      new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 0,
                      }).format(usd)
                    );
                  }
                  return parts.length > 0 ? parts.join(" | ") : "–";
                })()}
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3 mb-3">
            <div className="sr-count-card closed" onClick={() => navigateAdminDrilldown({})}>
              <div className="sr-count-label">Avg Queries per SR</div>
              <div className="sr-count-subtitle">Avg Queries per SR</div>
              <div className="sr-count-value">
                {adminDashboardData.avgQueriesPerSr != null &&
                adminDashboardData.avgQueriesPerSr !== ""
                  ? Number(adminDashboardData.avgQueriesPerSr).toFixed(2)
                  : "–"}
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 1: Open vs Closed Donut, SLA Compliance Gauge */}
        <div className="row charts-row mt-0">
          <div className="col-12 col-lg-4">
            <div className={`pie-chart-widget ${chartCollapsed.openVsClosed ? 'collapsed' : ''}`}>
              <div 
                className="pie-chart-header d-flex align-items-center justify-content-between gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleChart("openVsClosed");
                }}
                style={{ cursor: "pointer" }}
              >
                <h2 className="pie-chart-title mb-0 flex-grow-1">Open vs Closed</h2>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 chart-header-actions" onClick={(e) => e.stopPropagation()}>
                  {renderChartCardDateFilter("openVsClosed")}
                  <DashboardExportDropdown
                    onExcel={handleAdminOpenVsClosedExcel}
                    onPdf={handleAdminOpenVsClosedPdf}
                    onCsv={handleAdminOpenVsClosedCsv}
                  />
                </div>
                {chartCollapsed.openVsClosed ? (
                  <ExpandMoreIcon fontSize="small" className="text-muted flex-shrink-0" />
                ) : (
                  <ExpandLessIcon fontSize="small" className="text-muted flex-shrink-0" />
                )}
              </div>
              {!chartCollapsed.openVsClosed && (
              <div 
                className="pie-chart-container"
                style={{ cursor: "default" }}
              >
                {renderChartLoaderOverlay("openVsClosed")}
                <div className="pie-chart-wrapper">
                  <Doughnut
                    ref={openVsClosedChartRef}
                    data={openVsClosedChartData}
                    options={openVsClosedChartOptions}
                  />
                </div>
                <div className="pie-chart-legend">
                  <div className="legend-item">
                    <div className="legend-color pending"></div>
                    <span className="legend-label">Open</span>
                    <span className="legend-value">
                      {openBucketCount}
                    </span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color approved"></div>
                    <span className="legend-label">Closed</span>
                    <span className="legend-value">
                      {openVsClosedSrData?.closedSRCount || 0}
                    </span>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
          <div className="col-12 col-lg-4">
            <div className={`pie-chart-widget ${chartCollapsed.slaCompliance ? 'collapsed' : ''}`}>
              <div 
                className="pie-chart-header d-flex align-items-center justify-content-between gap-2"
                onClick={() => toggleChart("slaCompliance")}
                style={{ cursor: "pointer" }}
              >
                <h2 className="pie-chart-title mb-0 flex-grow-1">SLA Compliance %</h2>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 chart-header-actions" onClick={(e) => e.stopPropagation()}>
                  {renderChartCardDateFilter("sla")}
                  <DashboardExportDropdown
                    onExcel={handleAdminSlaExcel}
                    onPdf={handleAdminSlaPdf}
                    onCsv={handleAdminSlaCsv}
                  />
                </div>
                {chartCollapsed.slaCompliance ? (
                  <ExpandMoreIcon fontSize="small" className="text-muted flex-shrink-0" />
                ) : (
                  <ExpandLessIcon fontSize="small" className="text-muted flex-shrink-0" />
                )}
              </div>
              {!chartCollapsed.slaCompliance && (
              <div className="gauge-chart-container">
                {renderChartLoaderOverlay("sla")}
                <GaugeChart
                  value={Math.min(
                    100,
                    Math.max(0, Number(adminDashboardData.slaCompliancePercent) || 0)
                  )}
                  max={100}
                  title="SLA Compliance"
                />
              </div>
              )}
            </div>
          </div>
          <div className="col-12 col-lg-4">
            <div className={`pie-chart-widget ${chartCollapsed.srsByCategory ? 'collapsed' : ''}`}>
              <div 
                className="pie-chart-header d-flex align-items-center justify-content-between gap-2"
                onClick={() => toggleChart("srsByCategory")}
                style={{ cursor: "pointer" }}
              >
                <h2 className="pie-chart-title mb-0 flex-grow-1">SRs by Category</h2>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 chart-header-actions" onClick={(e) => e.stopPropagation()}>
                  {renderChartCardDateFilter("category")}
                  <DashboardExportDropdown
                    onExcel={handleAdminCategoryExcel}
                    onPdf={handleAdminCategoryPdf}
                    onCsv={handleAdminCategoryCsv}
                  />
                </div>
                {chartCollapsed.srsByCategory ? (
                  <ExpandMoreIcon fontSize="small" className="text-muted flex-shrink-0" />
                ) : (
                  <ExpandLessIcon fontSize="small" className="text-muted flex-shrink-0" />
                )}
              </div>
              {!chartCollapsed.srsByCategory && (
              <div className="pie-chart-container">
                {renderChartLoaderOverlay("category")}
                <div className="pie-chart-wrapper" style={{ cursor: "pointer" }}>
                  <Pie
                    ref={categoryChartRef}
                    data={categoryChartData}
                    options={categoryChartOptions}
                  />
                </div>
                <div className="pie-chart-legend">
                  {srsByCategoryData.labels.map((label, index) => (
                    <div key={index} className="legend-item">
                      <div 
                        className="legend-color" 
                        style={{ 
                          backgroundColor: categoryChartData.datasets[0].backgroundColor[index] 
                        }}
                      ></div>
                      <span className="legend-label">{label}</span>
                      <span className="legend-value">{srsByCategoryData.data[index]}</span>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Row 2: SRs by Zone, Avg TAT by Zone */}
        <div className="row charts-row">
          <div className="col-12 col-lg-6">
            <div className={`bar-chart-widget ${chartCollapsed.srsByZone ? 'collapsed' : ''}`}>
              <div 
                className="bar-chart-header d-flex align-items-center justify-content-between gap-2"
                onClick={() => toggleChart("srsByZone")}
                style={{ cursor: "pointer" }}
              >
                <h2 className="bar-chart-title mb-0 flex-grow-1">SRs by Zone</h2>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 chart-header-actions" onClick={(e) => e.stopPropagation()}>
                  {renderChartCardDateFilter("zone")}
                  <DashboardExportDropdown
                    onExcel={handleAdminZoneExcel}
                    onPdf={handleAdminZonePdf}
                    onCsv={handleAdminZoneCsv}
                  />
                </div>
                {chartCollapsed.srsByZone ? (
                  <ExpandMoreIcon fontSize="small" className="text-muted flex-shrink-0" />
                ) : (
                  <ExpandLessIcon fontSize="small" className="text-muted flex-shrink-0" />
                )}
              </div>
              {!chartCollapsed.srsByZone && (
              <div className={`bar-chart-wrapper${srsByZoneHasData ? "" : " is-empty"}`}>
                {renderChartLoaderOverlay("zone")}
                {srsByZoneHasData ? (
                  <Bar
                    ref={zoneChartRef}
                    data={zoneChartData}
                    options={zoneChartOptions}
                  />
                ) : (
                  <div className="chart-empty-state">No data available for the selected filters</div>
                )}
              </div>
              )}
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <div className={`bar-chart-widget ${chartCollapsed.avgTATByZone ? 'collapsed' : ''}`}>
              <div 
                className="bar-chart-header d-flex align-items-center justify-content-between gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleChart("avgTATByZone");
                }}
                style={{ cursor: "pointer" }}
              >
                <h2 className="bar-chart-title mb-0 flex-grow-1">Avg TAT by Zone</h2>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 chart-header-actions" onClick={(e) => e.stopPropagation()}>
                  {renderChartCardDateFilter("zone", "zoneAvgTat")}
                  <DashboardExportDropdown
                    onExcel={handleAdminAvgTatZoneExcel}
                    onPdf={handleAdminAvgTatZonePdf}
                    onCsv={handleAdminAvgTatZoneCsv}
                  />
                </div>
                {chartCollapsed.avgTATByZone ? (
                  <ExpandMoreIcon fontSize="small" className="text-muted flex-shrink-0" />
                ) : (
                  <ExpandLessIcon fontSize="small" className="text-muted flex-shrink-0" />
                )}
              </div>
              {!chartCollapsed.avgTATByZone && (
              <div className={`bar-chart-wrapper${avgTATByZoneHasData ? "" : " is-empty"}`}>
                {renderChartLoaderOverlay("zone")}
                {avgTATByZoneHasData ? (
                  <Bar
                    ref={avgTATByZoneChartRef}
                    data={avgTATByZoneChartData}
                    options={avgTATByZoneChartOptions}
                  />
                ) : (
                  <div className="chart-empty-state">No data available for the selected filters</div>
                )}
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Row 3: SRs by Business Vertical, Repeat Complaints */}
        <div className="row charts-row">
          <div className="col-12 col-lg-6">
            <div className={`bar-chart-widget ${chartCollapsed.srsByProduct ? 'collapsed' : ''}`}>
              <div 
                className="bar-chart-header d-flex align-items-center justify-content-between gap-2"
                onClick={() => toggleChart("srsByProduct")}
                style={{ cursor: "pointer" }}
              >
                <h2 className="bar-chart-title mb-0 flex-grow-1">SRs by Business Vertical</h2>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 chart-header-actions" onClick={(e) => e.stopPropagation()}>
                  {renderChartCardDateFilter("bv")}
                  <DashboardExportDropdown
                    onExcel={handleAdminProductExcel}
                    onPdf={handleAdminProductPdf}
                    onCsv={handleAdminProductCsv}
                  />
                </div>
                {chartCollapsed.srsByProduct ? (
                  <ExpandMoreIcon fontSize="small" className="text-muted flex-shrink-0" />
                ) : (
                  <ExpandLessIcon fontSize="small" className="text-muted flex-shrink-0" />
                )}
              </div>
              {!chartCollapsed.srsByProduct && (
              <div className="bar-chart-wrapper">
                {renderChartLoaderOverlay("bv")}
                <Bar
                  ref={productChartRef}
                  data={productChartData}
                  options={productChartOptions}
                />
              </div>
              )}
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <div className={`bar-chart-widget ${chartCollapsed.repeatComplaints ? 'collapsed' : ''}`}>
              <div 
                className="bar-chart-header d-flex align-items-center justify-content-between gap-2"
                onClick={() => toggleChart("repeatComplaints")}
                style={{ cursor: "pointer" }}
              >
                <h2 className="bar-chart-title mb-0 flex-grow-1">Repeat Complaints</h2>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 chart-header-actions" onClick={(e) => e.stopPropagation()}>
                  {renderChartCardDateFilter("repeat")}
                  <DashboardExportDropdown
                    onExcel={handleAdminRepeatExcel}
                    onPdf={handleAdminRepeatPdf}
                    onCsv={handleAdminRepeatCsv}
                  />
                </div>
                {chartCollapsed.repeatComplaints ? (
                  <ExpandMoreIcon fontSize="small" className="text-muted flex-shrink-0" />
                ) : (
                  <ExpandLessIcon fontSize="small" className="text-muted flex-shrink-0" />
                )}
              </div>
              {!chartCollapsed.repeatComplaints && (
              <div className="bar-chart-wrapper">
                {renderChartLoaderOverlay("repeat")}
                <Line
                  ref={repeatComplaintsChartRef}
                  data={repeatComplaintsChartData}
                  options={repeatComplaintsChartOptions}
                />
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Row 4: Settlement Type Split, SRs with Max Queries */}
        <div className="row charts-row">
          <div className="col-12 col-lg-6">
            <div className={`pie-chart-widget ${chartCollapsed.settlementType ? 'collapsed' : ''}`}>
              <div 
                className="pie-chart-header d-flex align-items-center justify-content-between gap-2"
                onClick={() => toggleChart("settlementType")}
                style={{ cursor: "pointer" }}
              >
                <h2 className="pie-chart-title mb-0 flex-grow-1">Settlement Type Split</h2>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 chart-header-actions" onClick={(e) => e.stopPropagation()}>
                  {renderChartCardDateFilter("settlement")}
                  <DashboardExportDropdown
                    onExcel={handleAdminSettlementExcel}
                    onPdf={handleAdminSettlementPdf}
                    onCsv={handleAdminSettlementCsv}
                  />
                </div>
                {chartCollapsed.settlementType ? (
                  <ExpandMoreIcon fontSize="small" className="text-muted flex-shrink-0" />
                ) : (
                  <ExpandLessIcon fontSize="small" className="text-muted flex-shrink-0" />
                )}
              </div>
              {!chartCollapsed.settlementType && (
              <div className="pie-chart-container">
                {renderChartLoaderOverlay("settlement")}
                {settlementTypeHasData ? (
                  <>
                    <div className="pie-chart-wrapper">
                      <Pie
                        ref={settlementTypeChartRef}
                        data={settlementTypeChartData}
                        options={settlementTypeChartOptions}
                      />
                    </div>
                    <div className="pie-chart-legend">
                      {settlementTypeData.labels.map((label, index) => (
                        <div key={index} className="legend-item">
                          <div
                            className="legend-color"
                            style={{
                              backgroundColor:
                                settlementTypeChartData.datasets[0].backgroundColor[index],
                            }}
                          ></div>
                          <span className="legend-label">{label}</span>
                          <span className="legend-value">{settlementTypeData.data[index]}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="chart-empty-state chart-empty-state-pie">
                    No data available for the selected filters
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <div className={`bar-chart-widget ${chartCollapsed.maxQueries ? 'collapsed' : ''}`}>
              <div 
                className="bar-chart-header d-flex align-items-center justify-content-between gap-2"
                onClick={() => toggleChart("maxQueries")}
                style={{ cursor: "pointer" }}
              >
                <h2 className="bar-chart-title mb-0 flex-grow-1">SRs with Max Queries</h2>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 chart-header-actions" onClick={(e) => e.stopPropagation()}>
                  {renderChartCardDateFilter("maxQueries")}
                  <DashboardExportDropdown
                    onExcel={handleAdminMaxQueriesExcel}
                    onPdf={handleAdminMaxQueriesPdf}
                    onCsv={handleAdminMaxQueriesCsv}
                  />
                </div>
                {chartCollapsed.maxQueries ? (
                  <ExpandMoreIcon fontSize="small" className="text-muted flex-shrink-0" />
                ) : (
                  <ExpandLessIcon fontSize="small" className="text-muted flex-shrink-0" />
                )}
              </div>
              {!chartCollapsed.maxQueries && (
              <div className="bar-chart-wrapper bar-chart-wrapper-max-queries">
                {renderChartLoaderOverlay("maxQueries")}
                <Bar
                  ref={maxQueriesChartRef}
                  data={maxQueriesChartData}
                  options={maxQueriesChartOptions}
                />
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Row 5: SRs with Max Queries */}
        <div className="row charts-row">
      
        </div>

        {/* Tables Row: SRs by Country, Pending Settlement, Reopened SRs */}
        <div className="row charts-row">
          {/* SRs by Country Table */}
          <div className="col-12 col-lg-4">
            <div className="bar-chart-widget">
              <div className="bar-chart-header d-flex align-items-center justify-content-between gap-2">
                <h2 className="bar-chart-title mb-0 flex-grow-1">SRs by Country</h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter("country")}
                  <DashboardExportDropdown
                  onExcel={handleAdminCountryExcel}
                  onPdf={handleAdminCountryPdf}
                  onCsv={handleAdminCountryCsv}
                />
                </div>
              </div>
              <div style={{ padding: "20px", background: "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)", borderRadius: "8px", boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.6)", position: "relative", overflow: "auto" }}>
                {renderChartLoaderOverlay("country")}
                <table className="admin-dashboard-table">
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>Count</th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {srsByCountryData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.country}</td>
                        <td>{item.count}</td>
                        <td>{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pending Settlement Table */}
          <div className="col-12 col-lg-4">
            <div className="bar-chart-widget">
              <div className="bar-chart-header d-flex align-items-center justify-content-between gap-2">
                <h2 className="bar-chart-title mb-0 flex-grow-1">Pending Settlement</h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter("pendingSettlement")}
                  <DashboardExportDropdown
                  onExcel={handleAdminPendingExcel}
                  onPdf={handleAdminPendingPdf}
                  onCsv={handleAdminPendingCsv}
                />
                </div>
              </div>
              <div style={{ padding: "20px", background: "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)", borderRadius: "8px", boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.6)", position: "relative", overflow: "auto" }}>
                {renderChartLoaderOverlay("pendingSettlement")}
                <table className="admin-dashboard-table">
                  <thead>
                    <tr>
                      <th>SR No</th>
                      <th>Amount</th>
                      <th>Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingSettlementData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.srNo}</td>
                        <td>{item.amount}</td>
                        <td>{item.daysPending}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Reopened SRs Table */}
          <div className="col-12 col-lg-4">
            <div className="bar-chart-widget">
              <div className="bar-chart-header d-flex align-items-center justify-content-between gap-2">
                <h2 className="bar-chart-title mb-0 flex-grow-1">Reopened SRs</h2>
                <div className="chart-header-actions">
                  {renderChartCardDateFilter("reopened")}
                  <DashboardExportDropdown
                  onExcel={handleAdminReopenedExcel}
                  onPdf={handleAdminReopenedPdf}
                  onCsv={handleAdminReopenedCsv}
                />
                </div>
              </div>
              <div style={{ padding: "20px", background: "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)", borderRadius: "8px", boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.6)", position: "relative", overflow: "auto" }}>
                {renderChartLoaderOverlay("reopened")}
                <table className="admin-dashboard-table">
                  <thead>
                    <tr>
                      <th>SR No</th>
                      <th>Date</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reopenedSRsData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.srNo}</td>
                        <td>{item.reopenedDate}</td>
                        <td>{item.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
